/**
 * Onboarding-specific translations
 * Separated for maintainability
 */

export const onboardingTranslations = {
  en: {
    // Native Language Step
    onboarding_native_language_title: "What is your native language?",
    onboarding_native_language_description: "Select your mother tongue to personalize your experience.",
    onboarding_more_languages: "More",
    onboarding_show_less_languages: "Less",

    // Target Language Step
    onboarding_target_language_title: "What do you want to learn?",
    onboarding_target_language_description: "Choose your target language. You'll hear a quick preview.",

    // Age Step
    onboarding_age_title: "Are you an adult?",
    onboarding_age_yes: "Yes",
    onboarding_age_no: "No",
    onboarding_age_adult_description: "18 years or older",
    onboarding_age_child_description: "Under 18 years",
    onboarding_age_description: "This helps us provide age-appropriate content.",

    // Level Step (CEFR)
    onboarding_level_title: "What is your level?",
    onboarding_level_description_cefr: "Select your proficiency level using the CEFR scale.",
    onboarding_level_unknown: "Not sure",
    onboarding_level_placement_hint: "We'll refine this after your placement test.",
    cefr_a1_description: "Complete beginner",
    cefr_a2_description: "Basic phrases & simple conversations",
    cefr_b1_description: "Can discuss familiar topics",
    cefr_b2_description: "Can interact with native speakers",
    cefr_c1_description: "Fluent for social & professional use",
    cefr_c2_description: "Near-native proficiency",

    // Pronoun Step
    onboarding_pronoun_title: "What are your pronouns?",
    onboarding_pronoun_description: "Some languages are gendered. This helps us speak to you accurately.",
    pronoun_masculine: "He/Him",
    pronoun_feminine: "She/Her",
    pronoun_nonbinary: "They/Them",
    pronoun_unspecified: "Prefer not to say",

    // Terms and Conditions
    terms_checkbox_label: "I agree to the",
    terms_and_conditions: "Terms and Conditions",

    // Completion Step
    onboarding_completion_title: "You're all set!",
    onboarding_completion_description: "Here's a summary of your preferences. You can always change these later in settings.",
    start_learning: "Start Learning",
    saving: "Saving...",
    redirecting: "Redirecting...",
    onboarding_privacy_note: "Your preferences are stored securely and help us personalize your learning experience.",

    // Learning Objective Step
    onboarding_objective_title: "What's your goal?",
    onboarding_objective_description: "Tell us what you want to achieve to help us tailor your path.",
    onboarding_objective_placeholder: 'e.g., "Presenting projects to clients in 6 months."',
    onboarding_objective_or: "or",
    onboarding_objective_record: "Record",
    onboarding_objective_stop: "Stop",
    onboarding_objective_start_recording: "Start voice recording",
    onboarding_objective_stop_recording: "Stop recording",
    onboarding_objective_transcribing: "Transcribing...",
    onboarding_objective_mic_denied: "Microphone access denied. Please allow microphone access and try again.",
    onboarding_objective_transcription_error: "Failed to transcribe audio. Please try again or type your objective.",
    onboarding_objective_skip_hint: "Optional. You can skip this for now.",
    onboarding_objective_entered: "You have entered a learning objective.",
    onboarding_objective_aria_label: "Your learning objective",

    // Notifications Step
    onboarding_notifications_title: "Stay on track",
    onboarding_finish: "Done",
    onboarding_notifications_description: "Consistent practice is key. We'll send friendly reminders to keep you moving.",
    onboarding_notifications_recommendation: "Just 10–15 minutes a day is more effective than long, infrequent sessions.",
    onboarding_notifications_audio: "Would you like me to send you practice reminders?",
    onboarding_notifications_yes: "Remind me",
    onboarding_notifications_no: "No thanks",
    onboarding_notifications_days_label: "Days",
    onboarding_notifications_time_label: "Time",
    onboarding_notifications_email_notice: "We'll send reminders to your email address.",
    day_mon: "Mon",
    day_tue: "Tue",
    day_wed: "Wed",
    day_thu: "Thu",
    day_fri: "Fri",
    day_sat: "Sat",
    day_sun: "Sun",
    notifications_summary_enabled: "Reminders enabled for {{count}} days at {{time}}",
    notifications_summary_disabled: "Reminders are disabled",

    // Calendar section
    onboarding_calendar_description: "Or add to your calendar",
    onboarding_calendar_google: "Google Calendar",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: "Download .ics",
    onboarding_calendar_outlook_hint: "Tip: Set as recurring in Outlook after adding",
    onboarding_calendar_added: "Added",

    // Email checkbox
    onboarding_email_checkbox_label: "Send me email reminders",

    // Warning
    onboarding_no_reminders_warning: "No reminders set. Consistency helps you learn faster!",

    // Onboarding step titles
    onboarding_step_native_language: "Native Language",
    onboarding_step_target_language: "Target Language",
    onboarding_step_age: "Age",
    onboarding_step_level: "Level",
    onboarding_step_pronoun: "Pronoun",
    onboarding_step_objective: "Objective",
    onboarding_step_notifications: "Reminders",
    onboarding_step_completion: "Complete",

    // V2 Steps
    onboarding_welcome_title: "Welcome!",
    onboarding_welcome_text: "Let's get started with a few questions and your first work-related conversation.",
    onboarding_welcome_duration: "Setup takes about 2 minutes, then a short first conversation.",
    onboarding_step_ready: "Ready",
    onboarding_ready_title: "You're all set!",
    onboarding_ready_description: "Next, you'll have a short first conversation so I can get to know you and teach you your first word.",
    onboarding_ready_learning: "Learning",
    onboarding_ready_level: "Level",
    onboarding_ready_start: "Start my first conversation",
    onboarding_ready_not_now: "Not now",
    onboarding_mic_listening: "Listening… say a few words",
    onboarding_mic_hear_yes: "I can hear it",
    onboarding_mic_hear_no: "I can't hear it",
    onboarding_mic_troubleshoot: "No worries — check that the correct microphone is selected and your volume is up, then record again.",
    onboarding_mic_playback_confirm: "Listen to your recording — can you hear yourself?",
    onboarding_accept_continue: "Accept & Continue",
    onboarding_back: "Back",
    onboarding_how_it_works_title: "How it works",
    onboarding_how_it_works_text: "I understand both your native and target languages. I'll use both to help you learn, but try to use your new language as much as possible!",
    onboarding_level_beginner_description: "Beginner",
    level_beginner: "Beginner",
    level_intermediate: "Intermediate",
    level_advanced: "Advanced",
    level_beginner_description: "I can understand and use basic phrases and expressions",
    level_intermediate_description: "I can have conversations about familiar topics and describe experiences",
    level_advanced_description: "I can express myself fluently and spontaneously in most situations",
    onboarding_level_title_v2: "What are you comfortable doing in {{language}}?",
    onboarding_level_question: "What are you comfortable doing in {{language}}?",
    language_fr: "French",
    language_en: "English",
    language_es: "Spanish",
    onboarding_level_intermediate_description: "Intermediate",
    onboarding_level_advanced_description: "Advanced",
    onboarding_adaptation_title: "How I'll adapt to you",
    onboarding_adaptation_beginner: "At the **{{level}}** level, I'll use {{nativeLanguage}} for support while gradually introducing {{targetLanguage}} basics.",
    onboarding_adaptation_intermediate: "At the **{{level}}** level, I'll speak mostly in {{targetLanguage}}, using {{nativeLanguage}} only for complex explanations.",
    onboarding_adaptation_advanced: "At the **{{level}}** level, we'll stick to {{targetLanguage}} to refine your professional fluency and idioms.",
    onboarding_adaptation_advanced_tip: "💡 Try **Realtime mode** in Settings for a faster, uninterrupted experience.",
    onboarding_mic_title: "Test your mic",
    onboarding_mic_description: "Let's make sure we can hear you for your conversations.",
    onboarding_mic_permission_prompt: "Please grant microphone access.",
    onboarding_mic_allow: "Allow Microphone",
    onboarding_mic_requesting: "Requesting access...",
    onboarding_mic_denied: "Access denied. Please enable the microphone in your browser settings.",
    onboarding_mic_retry: "Try Again",
    onboarding_mic_skip: "Skip",
    onboarding_mic_ready: "Ready! Click record and say a few words.",
    onboarding_mic_granted: "Access granted! Click below to record a test.",
    onboarding_mic_tip: "Tip: Use headphones and a quiet place for best results.",
    onboarding_mic_recording: "Recording... Click to stop",
    onboarding_mic_play_back: "Great! Listen to your recording:",
    onboarding_mic_success: "Microphone working perfectly!",
    onboarding_mic_record_again: "Record again",
    onboarding_conversation_title: "First conversation",
    onboarding_conversation_start_prompt: "Click the button and say **{{greeting}}** to begin!",
    onboarding_conversation_done: "Well done!",
    onboarding_conversation_complete_hint: "Building your personalized plan...",
    onboarding_recap_title: "First session complete!",
    onboarding_recap_description: "You've taken your first step towards fluency in {{language}}.",
    onboarding_recap_accomplished: "Summary:",
    onboarding_recap_had_conversation: "Completed your first conversation",
    onboarding_recap_learned_word: "Learned professional vocabulary",
    onboarding_recap_personalized: "Personalized plan created",
    onboarding_auto_objective: "Professional {{language}} based on intro conversation",
    onboarding_save_error: "Error. Progress saved locally.",
    onboarding_achievement_title: "Day 1 of {{language}} complete!",
    onboarding_achievement_summary: "Your path is ready. Start your journey anytime!",
    onboarding_achievement_continue: "Continue",
    onboarding_achievement_stop: "Stop for today",
  },

  ro: {
    // Native Language Step
    onboarding_native_language_title: "Care este limba ta maternă?",
    onboarding_native_language_description: "Selectează limba ta maternă pentru a-ți personaliza experiența.",
    onboarding_more_languages: "Mai multe",
    onboarding_show_less_languages: "Mai puține",

    // Target Language Step
    onboarding_target_language_title: "Ce vrei să înveți?",
    onboarding_target_language_description: "Alege limba pe care vrei să o înveți. Vei auzi o scurtă previzualizare.",

    // Age Step
    onboarding_age_title: "Ești adult?",
    onboarding_age_yes: "Da",
    onboarding_age_no: "Nu",
    onboarding_age_adult_description: "18 ani sau mai mult",
    onboarding_age_child_description: "Sub 18 ani",
    onboarding_age_description: "Acest lucru ne ajută să oferim conținut adecvat vârstei.",

    // Level Step (CEFR)
    onboarding_level_title: "Care este nivelul tău?",
    onboarding_level_description_cefr: "Selectează nivelul tău de competență folosind scala CEFR.",
    onboarding_level_unknown: "Nu sunt sigur",
    onboarding_level_placement_hint: "Vom ajusta acest lucru după testul de plasare.",
    cefr_a1_description: "Începător complet",
    cefr_a2_description: "Fraze de bază și conversații simple",
    cefr_b1_description: "Poate discuta subiecte familiare",
    cefr_b2_description: "Poate interacționa cu vorbitori nativi",
    cefr_c1_description: "Fluent pentru uz social și profesional",
    cefr_c2_description: "Competență aproape nativă",

    // Pronoun Step
    onboarding_pronoun_title: "Care sunt pronumele tale?",
    onboarding_pronoun_description: "Unele limbi folosesc genul. Acest lucru ne ajută să ne adresăm corect.",
    pronoun_masculine: "El",
    pronoun_feminine: "Ea",
    pronoun_nonbinary: "El/Ea (nebinar)",
    pronoun_unspecified: "Prefer să nu spun",

    // Terms and Conditions
    terms_checkbox_label: "Sunt de acord cu",
    terms_and_conditions: "Termenii și condițiile",

    // Completion Step
    onboarding_completion_title: "Totul este pregătit!",
    onboarding_completion_description: "Iată un rezumat al preferințelor tale. Le poți modifica oricând în setări.",
    start_learning: "Începe să înveți",
    saving: "Se salvează...",
    redirecting: "Se redirecționează...",
    onboarding_privacy_note: "Preferințele tale sunt stocate în siguranță și ne ajută să personalizăm experiența ta de învățare.",

    // Learning Objective Step
    onboarding_objective_title: "Care este obiectivul tău?",
    onboarding_objective_description: "Spune-ne ce vrei să realizezi pentru a-ți adapta parcursul.",
    onboarding_objective_placeholder: 'de ex., „Să prezint proiecte clienților în 6 luni.”',
    onboarding_objective_or: "sau",
    onboarding_objective_record: "Înregistrează",
    onboarding_objective_stop: "Oprește",
    onboarding_objective_start_recording: "Începe înregistrarea vocală",
    onboarding_objective_stop_recording: "Oprește înregistrarea",
    onboarding_objective_transcribing: "Se transcrie...",
    onboarding_objective_mic_denied: "Accesul la microfon a fost refuzat. Permite accesul la microfon și încearcă din nou.",
    onboarding_objective_transcription_error: "Transcrierea audio a eșuat. Încearcă din nou sau scrie obiectivul tău.",
    onboarding_objective_skip_hint: "Opțional. Poți omite acest pas deocamdată.",
    onboarding_objective_entered: "Ai introdus un obiectiv de învățare.",
    onboarding_objective_aria_label: "Obiectivul tău de învățare",

    // Notifications Step
    onboarding_notifications_title: "Rămâi pe drumul cel bun",
    onboarding_finish: "Gata",
    onboarding_notifications_description: "Practica constantă este esențială. Îți vom trimite mementouri prietenoase ca să continui.",
    onboarding_notifications_recommendation: "Doar 10–15 minute pe zi sunt mai eficiente decât sesiuni lungi și rare.",
    onboarding_notifications_audio: "Vrei să îți trimit mementouri pentru practică?",
    onboarding_notifications_yes: "Amintește-mi",
    onboarding_notifications_no: "Nu, mulțumesc",
    onboarding_notifications_days_label: "Zile",
    onboarding_notifications_time_label: "Ora",
    onboarding_notifications_email_notice: "Vom trimite mementouri la adresa ta de e-mail.",
    day_mon: "Lun",
    day_tue: "Mar",
    day_wed: "Mie",
    day_thu: "Joi",
    day_fri: "Vin",
    day_sat: "Sâm",
    day_sun: "Dum",
    notifications_summary_enabled: "Mementouri activate pentru {{count}} zile la ora {{time}}",
    notifications_summary_disabled: "Mementourile sunt dezactivate",

    // Calendar section
    onboarding_calendar_description: "Sau adaugă în calendarul tău",
    onboarding_calendar_google: "Google Calendar",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: "Descarcă .ics",
    onboarding_calendar_outlook_hint: "Sfat: Setează ca recurent în Outlook după adăugare",
    onboarding_calendar_added: "Adăugat",

    // Email checkbox
    onboarding_email_checkbox_label: "Trimite-mi mementouri prin e-mail",

    // Warning
    onboarding_no_reminders_warning: "Niciun memento setat. Consecvența te ajută să înveți mai repede!",

    // Onboarding step titles
    onboarding_step_native_language: "Limba maternă",
    onboarding_step_target_language: "Limba țintă",
    onboarding_step_age: "Vârstă",
    onboarding_step_level: "Nivel",
    onboarding_step_pronoun: "Pronume",
    onboarding_step_objective: "Obiectiv",
    onboarding_step_notifications: "Mementouri",
    onboarding_step_completion: "Finalizare",

    // V2 Steps
    onboarding_welcome_title: "Bine ai venit!",
    onboarding_welcome_text: "Să începem cu câteva întrebări și prima ta conversație legată de muncă.",
    onboarding_welcome_duration: "Configurarea durează aproximativ 2 minute, apoi o scurtă primă conversație.",
    onboarding_step_ready: "Pregătit",
    onboarding_ready_title: "Totul este pregătit!",
    onboarding_ready_description: "În continuare, vei avea o scurtă primă conversație ca să te pot cunoaște și să te învăț primul cuvânt.",
    onboarding_ready_learning: "Înveți",
    onboarding_ready_level: "Nivel",
    onboarding_ready_start: "Începe prima mea conversație",
    onboarding_ready_not_now: "Nu acum",
    onboarding_mic_listening: "Ascult… spune câteva cuvinte",
    onboarding_mic_hear_yes: "Se aude",
    onboarding_mic_hear_no: "Nu se aude",
    onboarding_mic_troubleshoot: "Nicio grijă — verifică dacă este selectat microfonul corect și dacă volumul este ridicat, apoi înregistrează din nou.",
    onboarding_mic_playback_confirm: "Ascultă înregistrarea ta — te poți auzi?",
    onboarding_accept_continue: "Acceptă și continuă",
    onboarding_back: "Înapoi",
    onboarding_how_it_works_title: "Cum funcționează",
    onboarding_how_it_works_text: "Înțeleg atât limba ta maternă, cât și limba țintă. Le voi folosi pe amândouă ca să te ajut să înveți, dar încearcă să folosești limba nouă cât mai mult posibil!",
    onboarding_level_beginner_description: "Începător",
    level_beginner: "Începător",
    level_intermediate: "Intermediar",
    level_advanced: "Avansat",
    level_beginner_description: "Pot înțelege și folosi fraze și expresii de bază",
    level_intermediate_description: "Pot purta conversații despre subiecte familiare și descrie experiențe",
    level_advanced_description: "Mă pot exprima fluent și spontan în majoritatea situațiilor",
    onboarding_level_title_v2: "Ce te simți confortabil să faci în {{language}}?",
    onboarding_level_question: "Ce te simți confortabil să faci în {{language}}?",
    language_fr: "Franceză",
    language_en: "Engleză",
    language_es: "Spaniolă",
    onboarding_level_intermediate_description: "Intermediar",
    onboarding_level_advanced_description: "Avansat",
    onboarding_adaptation_title: "Cum mă voi adapta la tine",
    onboarding_adaptation_beginner: "La nivelul **{{level}}**, voi folosi {{nativeLanguage}} pentru sprijin, introducând treptat noțiunile de bază în {{targetLanguage}}.",
    onboarding_adaptation_intermediate: "La nivelul **{{level}}**, voi vorbi mai ales în {{targetLanguage}}, folosind {{nativeLanguage}} doar pentru explicații complexe.",
    onboarding_adaptation_advanced: "La nivelul **{{level}}**, vom rămâne la {{targetLanguage}} pentru a-ți rafina fluența profesională și expresiile idiomatice.",
    onboarding_adaptation_advanced_tip: "💡 Încearcă **modul în timp real** din Setări pentru o experiență mai rapidă și neîntreruptă.",
    onboarding_mic_title: "Testează-ți microfonul",
    onboarding_mic_description: "Să ne asigurăm că te putem auzi în conversațiile tale.",
    onboarding_mic_permission_prompt: "Te rugăm să acorzi acces la microfon.",
    onboarding_mic_allow: "Permite microfonul",
    onboarding_mic_requesting: "Se solicită accesul...",
    onboarding_mic_denied: "Acces refuzat. Activează microfonul din setările browserului.",
    onboarding_mic_retry: "Încearcă din nou",
    onboarding_mic_skip: "Omite",
    onboarding_mic_ready: "Gata! Apasă pe înregistrare și spune câteva cuvinte.",
    onboarding_mic_granted: "Acces acordat! Apasă mai jos pentru a înregistra un test.",
    onboarding_mic_tip: "Sfat: Folosește căști și un loc liniștit pentru cele mai bune rezultate.",
    onboarding_mic_recording: "Se înregistrează... Apasă pentru a opri",
    onboarding_mic_play_back: "Grozav! Ascultă înregistrarea ta:",
    onboarding_mic_success: "Microfonul funcționează perfect!",
    onboarding_mic_record_again: "Înregistrează din nou",
    onboarding_conversation_title: "Prima conversație",
    onboarding_conversation_start_prompt: "Apasă butonul și spune **{{greeting}}** pentru a începe!",
    onboarding_conversation_done: "Bravo!",
    onboarding_conversation_complete_hint: "Se construiește planul tău personalizat...",
    onboarding_recap_title: "Prima sesiune finalizată!",
    onboarding_recap_description: "Ai făcut primul pas către fluența în {{language}}.",
    onboarding_recap_accomplished: "Rezumat:",
    onboarding_recap_had_conversation: "Ai finalizat prima ta conversație",
    onboarding_recap_learned_word: "Ai învățat vocabular profesional",
    onboarding_recap_personalized: "Plan personalizat creat",
    onboarding_auto_objective: "{{language}} profesională pe baza conversației introductive",
    onboarding_save_error: "Eroare. Progresul a fost salvat local.",
    onboarding_achievement_title: "Ziua 1 de {{language}} finalizată!",
    onboarding_achievement_summary: "Parcursul tău este gata. Începe-ți călătoria oricând!",
    onboarding_achievement_continue: "Continuă",
    onboarding_achievement_stop: "Oprește-te pentru azi",
  },

  fr: {
    // Native Language Step
    onboarding_native_language_title: "Quelle est votre langue maternelle ?",
    onboarding_native_language_description: "Sélectionnez votre langue pour personnaliser l'expérience.",
    onboarding_more_languages: "Plus",
    onboarding_show_less_languages: "Moins",

    // Target Language Step
    onboarding_target_language_title: "Quelle langue apprendre ?",
    onboarding_target_language_description: "Choisissez la langue cible pour entendre un aperçu.",

    // Age Step
    onboarding_age_title: "Êtes-vous un adulte ?",
    onboarding_age_yes: "Oui",
    onboarding_age_no: "Non",
    onboarding_age_adult_description: "18 ans ou plus",
    onboarding_age_child_description: "Moins de 18 ans",
    onboarding_age_description: "Cela nous aide à vous proposer un contenu adapté à votre âge.",

    // Level Step (CEFR)
    onboarding_level_title: "Quel est votre niveau ?",
    onboarding_level_description_cefr: "Sélectionnez votre niveau selon l'échelle CECR.",
    onboarding_level_unknown: "Je ne sais pas",
    onboarding_level_placement_hint: "Nous affinerons cela après votre test de placement.",
    cefr_a1_description: "Débutant complet",
    cefr_a2_description: "Phrases simples et conversations basiques",
    cefr_b1_description: "Peut discuter de sujets familiers",
    cefr_b2_description: "Peut interagir avec des locuteurs natifs",
    cefr_c1_description: "Courant pour usage social et professionnel",
    cefr_c2_description: "Niveau quasi natif",

    // Pronoun Step
    onboarding_pronoun_title: "Quels sont vos pronoms ?",
    onboarding_pronoun_description: "Certaines langues sont genrées. Cela nous aide à vous parler correctement.",
    pronoun_masculine: "Il/Lui",
    pronoun_feminine: "Elle",
    pronoun_nonbinary: "Iel",
    pronoun_unspecified: "Préfère ne pas dire",

    // Terms and Conditions
    terms_checkbox_label: "J'accepte les",
    terms_and_conditions: "Conditions générales",

    // Completion Step
    onboarding_completion_title: "Vous êtes prêt !",
    onboarding_completion_description: "Voici un résumé de vos préférences. Vous pouvez les modifier plus tard dans les paramètres.",
    start_learning: "Commencer à apprendre",
    saving: "Enregistrement...",
    redirecting: "Redirection...",
    onboarding_privacy_note: "Vos préférences sont stockées de manière sécurisée et nous aident à personnaliser votre expérience d'apprentissage.",

    // Learning Objective Step
    onboarding_objective_title: "Votre objectif ?",
    onboarding_objective_description: "Dites-nous ce que vous visez pour personnaliser votre parcours.",
    onboarding_objective_placeholder: 'ex: "Présenter un projet en 6 mois."',
    onboarding_objective_or: "ou",
    onboarding_objective_record: "Enregistrer",
    onboarding_objective_stop: "Arrêter",
    onboarding_objective_start_recording: "Démarrer l'enregistrement vocal",
    onboarding_objective_stop_recording: "Arrêter l'enregistrement",
    onboarding_objective_transcribing: "Transcription en cours...",
    onboarding_objective_mic_denied: "Accès au microphone refusé. Veuillez autoriser l'accès au microphone et réessayer.",
    onboarding_objective_transcription_error: "Échec de la transcription audio. Veuillez réessayer ou saisir votre objectif.",
    onboarding_objective_skip_hint: "Optionnel. Vous pourrez le définir plus tard.",
    onboarding_objective_entered: "Vous avez saisi un objectif d'apprentissage.",
    onboarding_objective_aria_label: "Votre objectif d'apprentissage",

    // Notifications Step
    onboarding_notifications_title: "Restez régulier",
    onboarding_finish: "Terminé",
    onboarding_notifications_description: "La clé est la régularité. Nous vous enverrons des rappels pour vous motiver.",
    onboarding_notifications_recommendation: "10 à 15 minutes par jour sont plus efficaces que de longues séances rares.",
    onboarding_notifications_audio: "Voulez-vous que je vous envoie des rappels de pratique ?",
    onboarding_notifications_yes: "Rappelez-moi",
    onboarding_notifications_no: "Non merci",
    onboarding_notifications_days_label: "Jours",
    onboarding_notifications_time_label: "Heure",
    onboarding_notifications_email_notice: "Nous enverrons les rappels à votre adresse e-mail.",
    day_mon: "Lun",
    day_tue: "Mar",
    day_wed: "Mer",
    day_thu: "Jeu",
    day_fri: "Ven",
    day_sat: "Sam",
    day_sun: "Dim",
    notifications_summary_enabled: "Rappels activés pour {{count}} jours à {{time}}",
    notifications_summary_disabled: "Les rappels sont désactivés",

    // Calendar section
    onboarding_calendar_description: "Ou ajouter à votre calendrier",
    onboarding_calendar_google: "Google Agenda",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: "Télécharger .ics",
    onboarding_calendar_outlook_hint: "Conseil : Définir comme récurrent dans Outlook après l'ajout",
    onboarding_calendar_added: "Ajouté",

    // Email checkbox
    onboarding_email_checkbox_label: "M'envoyer des rappels par e-mail",

    // Warning
    onboarding_no_reminders_warning: "Aucun rappel. La régularité accélère l'apprentissage !",

    // Onboarding step titles
    onboarding_step_native_language: "Langue maternelle",
    onboarding_step_target_language: "Langue à apprendre",
    onboarding_step_age: "Âge",
    onboarding_step_level: "Niveau",
    onboarding_step_pronoun: "Pronoms",
    onboarding_step_objective: "Objectif",
    onboarding_step_notifications: "Rappels",
    onboarding_step_completion: "Compléter",

    // V2 Steps
    onboarding_welcome_title: "Bienvenue !",
    onboarding_welcome_text: "Commençons par quelques questions et votre première conversation professionnelle.",
    onboarding_welcome_duration: "La configuration prend environ 2 minutes, puis une courte première conversation.",
    onboarding_step_ready: "Prêt",
    onboarding_ready_title: "Vous êtes prêt !",
    onboarding_ready_description: "Ensuite, vous aurez une courte première conversation pour que je puisse faire votre connaissance et vous apprendre votre premier mot.",
    onboarding_ready_learning: "Apprentissage",
    onboarding_ready_level: "Niveau",
    onboarding_ready_start: "Démarrer ma première conversation",
    onboarding_ready_not_now: "Pas maintenant",
    onboarding_mic_listening: "J'écoute… dites quelques mots",
    onboarding_mic_hear_yes: "Je m'entends",
    onboarding_mic_hear_no: "Je ne m'entends pas",
    onboarding_mic_troubleshoot: "Pas de souci — vérifiez que le bon microphone est sélectionné et que le volume est activé, puis réenregistrez.",
    onboarding_mic_playback_confirm: "Écoutez votre enregistrement — vous entendez-vous ?",
    onboarding_accept_continue: "Accepter et continuer",
    onboarding_back: "Retour",
    onboarding_how_it_works_title: "Comment ça marche",
    onboarding_how_it_works_text: "Je comprends votre langue maternelle et celle que vous apprenez. Je parlerai les deux pour vous aider, mais essayez d'utiliser votre nouvelle langue au maximum !",
    onboarding_level_beginner_description: "Débutant",
    level_beginner: "Débutant",
    level_intermediate: "Intermédiaire",
    level_advanced: "Avancé",
    level_beginner_description: "Je peux comprendre et utiliser des phrases et expressions de base",
    level_intermediate_description: "Je peux avoir des conversations sur des sujets familiers et décrire des expériences",
    level_advanced_description: "Je peux m'exprimer couramment et spontanément dans la plupart des situations",
    onboarding_level_title_v2: "Que pouvez-vous faire en {{language}} ?",
    onboarding_level_question: "Que pouvez-vous faire en {{language}} ?",
    language_fr: "français",
    language_en: "anglais",
    language_es: "espagnol",
    onboarding_level_intermediate_description: "Intermédiaire",
    onboarding_level_advanced_description: "Avancé",
    onboarding_adaptation_title: "Mon adaptation",
    onboarding_adaptation_beginner: "Au niveau **{{level}}**, je vous aiderai en {{nativeLanguage}} tout en introduisant les bases en {{targetLanguage}}.",
    onboarding_adaptation_intermediate: "Au niveau **{{level}}**, je parlerai surtout en {{targetLanguage}} avec des explications en {{nativeLanguage}} si nécessaire.",
    onboarding_adaptation_advanced: "Au niveau **{{level}}**, nous resterons en {{targetLanguage}} pour perfectionner votre fluidité professionnelle.",
    onboarding_adaptation_advanced_tip: "💡 Essayez le **mode Temps réel** dans les paramètres pour une conversation plus fluide.",
    onboarding_mic_title: "Test du micro",
    onboarding_mic_description: "Vérifions que votre micro est prêt pour discuter.",
    onboarding_mic_permission_prompt: "L'accès au micro est requis.",
    onboarding_mic_allow: "Autoriser le micro",
    onboarding_mic_requesting: "Demande d'accès...",
    onboarding_mic_denied: "Accès refusé. Veuillez activer le micro dans les paramètres du navigateur.",
    onboarding_mic_retry: "Réessayer",
    onboarding_mic_skip: "Passer",
    onboarding_mic_ready: "Prêt ! Enregistrez quelques mots pour tester.",
    onboarding_mic_granted: "Accès autorisé ! Cliquez ci-dessous pour tester.",
    onboarding_mic_tip: "Conseil : Utilisez des écouteurs et un endroit calme pour de meilleurs résultats.",
    onboarding_mic_recording: "Enregistrement... Cliquez pour arrêter",
    onboarding_mic_play_back: "Super ! Écoutez votre test :",
    onboarding_mic_success: "Le micro fonctionne parfaitement !",
    onboarding_mic_record_again: "Recommencer",
    onboarding_conversation_title: "Première conversation",
    onboarding_conversation_start_prompt: "Cliquez et dites **{{greeting}}** pour commencer !",
    onboarding_conversation_done: "Bravo !",
    onboarding_conversation_complete_hint: "Préparation de votre plan personnalisé...",
    onboarding_recap_title: "Session terminée !",
    onboarding_recap_description: "Premier pas réussi vers la maîtrise du {{language}}.",
    onboarding_recap_accomplished: "Résumé :",
    onboarding_recap_had_conversation: "Première conversation terminée",
    onboarding_recap_learned_word: "Vocabulaire pro acquis",
    onboarding_recap_personalized: "Plan d'apprentissage créé",
    onboarding_auto_objective: "{{language}} professionnel selon conversation initiale",
    onboarding_save_error: "Erreur. Progression sauvegardée localement.",
    onboarding_achievement_title: "Jour 1 de {{language}} terminé !",
    onboarding_achievement_summary: "Votre parcours est prêt. Continuez quand vous voulez !",
    onboarding_achievement_continue: "Continuer",
    onboarding_achievement_stop: "Arrêter pour aujourd'hui",
  },

  es: {
    // Native Language Step
    onboarding_native_language_title: "¿Cuál es tu lengua materna?",
    onboarding_native_language_description: "Selecciona tu lengua materna para personalizar tu experiencia.",
    onboarding_more_languages: "Más idiomas",
    onboarding_show_less_languages: "Menos idiomas",

    // Target Language Step
    onboarding_target_language_title: "¿Qué idioma quieres aprender?",
    onboarding_target_language_description: "Elige el idioma que deseas aprender. Escucharás un saludo de muestra.",

    // Age Step
    onboarding_age_title: "¿Eres adulto?",
    onboarding_age_yes: "Sí",
    onboarding_age_no: "No",
    onboarding_age_adult_description: "18 años o más",
    onboarding_age_child_description: "Menor de 18 años",
    onboarding_age_description: "Esto nos ayuda a ofrecerte contenido apropiado para tu edad.",

    // Level Step (CEFR)
    onboarding_level_title: "¿Cuál es tu nivel actual?",
    onboarding_level_description_cefr: "Selecciona tu nivel de competencia usando la escala MCER.",
    onboarding_level_unknown: "No lo sé",
    onboarding_level_placement_hint: "Determinaremos tu nivel en tu primera conversación.",
    cefr_a1_description: "Principiante completo",
    cefr_a2_description: "Frases básicas y conversaciones simples",
    cefr_b1_description: "Puede discutir temas familiares",
    cefr_b2_description: "Puede interactuar con hablantes nativos",
    cefr_c1_description: "Fluidez social y profesional",
    cefr_c2_description: "Nivel casi nativo",

    // Pronoun Step
    onboarding_pronoun_title: "¿Cuáles son tus pronombres?",
    onboarding_pronoun_description: "Algunos idiomas tienen género. Esto nos ayuda a hablarte con precisión.",
    pronoun_masculine: "Él",
    pronoun_feminine: "Ella",
    pronoun_nonbinary: "Elle",
    pronoun_unspecified: "Prefiero no decir",

    // Terms and Conditions
    terms_checkbox_label: "Acepto los",
    terms_and_conditions: "Términos y Condiciones",

    // Completion Step
    onboarding_completion_title: "¡Todo listo!",
    onboarding_completion_description: "Aquí tienes un resumen de tus preferencias. Puedes cambiarlas más tarde en configuración.",
    start_learning: "Empezar a aprender",
    saving: "Guardando...",
    redirecting: "Redirigiendo...",
    onboarding_privacy_note: "Tus preferencias se almacenan de forma segura y nos ayudan a personalizar tu experiencia de aprendizaje.",

    // Learning Objective Step
    onboarding_objective_title: "Última cosa: ¿Cuál es tu objetivo de aprendizaje?",
    onboarding_objective_description: 'Comparte tu objetivo para ayudarnos a personalizar tu camino de aprendizaje. Incluye un plazo y un ejemplo concreto si posible.',
    onboarding_objective_placeholder: 'ej. "Quiero poder presentar a clientes en francés en 6 meses..."',
    onboarding_objective_or: "o",
    onboarding_objective_record: "Grabar",
    onboarding_objective_stop: "Parar",
    onboarding_objective_start_recording: "Iniciar grabación de voz",
    onboarding_objective_stop_recording: "Detener grabación",
    onboarding_objective_transcribing: "Transcribiendo...",
    onboarding_objective_mic_denied: "Acceso al micrófono denegado. Por favor, permite el acceso al micrófono e inténtalo de nuevo.",
    onboarding_objective_transcription_error: "Error al transcribir el audio. Por favor, inténtalo de nuevo o escribe tu objetivo.",
    onboarding_objective_skip_hint: "Este paso es opcional. Puedes saltarlo si aún no estás seguro.",
    onboarding_objective_entered: "Has ingresado un objetivo de aprendizaje.",
    onboarding_objective_aria_label: "Tu objetivo de aprendizaje",

    // Notifications Step
    onboarding_notifications_title: "Mantente en camino con recordatorios de práctica",
    onboarding_finish: "Listo",
    onboarding_notifications_description: "La práctica regular es clave para aprender idiomas. Te enviaremos recordatorios amigables para ayudarte a mantener la constancia.",
    onboarding_notifications_recommendation: "Recomendamos sesiones cortas de 10 a 15 minutos cada día. Investigaciones de Stanford y Cambridge demuestran que la práctica breve y espaciada es más efectiva que las sesiones largas e infrecuentes.",
    onboarding_notifications_audio: "¿Te gustaría que te envíe recordatorios de práctica?",
    onboarding_notifications_yes: "Sí, recuérdame",
    onboarding_notifications_no: "No, gracias",
    onboarding_notifications_days_label: "¿Qué días?",
    onboarding_notifications_time_label: "¿A qué hora?",
    onboarding_notifications_email_notice: "Enviaremos recordatorios a tu dirección de correo electrónico.",
    day_mon: "Lun",
    day_tue: "Mar",
    day_wed: "Mié",
    day_thu: "Jue",
    day_fri: "Vie",
    day_sat: "Sáb",
    day_sun: "Dom",
    notifications_summary_enabled: "Recordatorios activados para {{count}} días a las {{time}}",
    notifications_summary_disabled: "Los recordatorios están desactivados",

    // Calendar section
    onboarding_calendar_description: "O agregar a tu calendario",
    onboarding_calendar_google: "Google Calendar",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: "Descargar .ics",
    onboarding_calendar_outlook_hint: "Consejo: Configura como recurrente en Outlook después de agregar",
    onboarding_calendar_added: "Agregado",

    // Email checkbox
    onboarding_email_checkbox_label: "Enviarme recordatorios por correo electrónico",

    // Warning
    onboarding_no_reminders_warning: "No hay recordatorios configurados. ¡La práctica constante te ayuda a aprender más rápido!",

    // Onboarding step titles
    onboarding_step_native_language: "Lengua materna",
    onboarding_step_target_language: "Idioma a aprender",
    onboarding_step_age: "Edad",
    onboarding_step_level: "Nivel",
    onboarding_step_pronoun: "Pronombres",
    onboarding_step_objective: "Objetivo",
    onboarding_step_notifications: "Recordatorios",
    onboarding_step_completion: "Completar",

    // V2 Steps
    onboarding_welcome_title: "¡Bienvenido!",
    onboarding_welcome_text: "Comencemos con algunas preguntas y tu primera conversación relacionada con el trabajo.",
    onboarding_welcome_duration: "La configuración toma unos 2 minutos, luego una breve primera conversación.",
    onboarding_step_ready: "Listo",
    onboarding_ready_title: "¡Todo listo!",
    onboarding_ready_description: "A continuación, tendrás una breve primera conversación para que pueda conocerte y enseñarte tu primera palabra.",
    onboarding_ready_learning: "Aprendiendo",
    onboarding_ready_level: "Nivel",
    onboarding_ready_start: "Comenzar mi primera conversación",
    onboarding_ready_not_now: "Ahora no",
    onboarding_mic_listening: "Escuchando… di unas palabras",
    onboarding_mic_hear_yes: "Me escucho",
    onboarding_mic_hear_no: "No me escucho",
    onboarding_mic_troubleshoot: "No te preocupes — comprueba que esté seleccionado el micrófono correcto y que el volumen esté alto, luego graba de nuevo.",
    onboarding_mic_playback_confirm: "Escucha tu grabación — ¿puedes oírte?",
    onboarding_accept_continue: "Aceptar y continuar",
    onboarding_back: "Atrás",
    onboarding_how_it_works_title: "Cómo funciona",
    onboarding_how_it_works_text: "Entiendo tanto tu idioma nativo como el que estás aprendiendo. Usaré ambos para ayudarte, ¡pero intenta usar tu nuevo idioma lo más posible!",
    onboarding_level_beginner_description: "Habilidad oral para Principiante",
    level_beginner: "Principiante",
    level_intermediate: "Intermedio",
    level_advanced: "Avanzado",
    level_beginner_description: "Puedo entender y usar frases y expresiones básicas",
    level_intermediate_description: "Puedo tener conversaciones sobre temas familiares y describir experiencias",
    level_advanced_description: "Puedo expresarme con fluidez y espontaneidad en la mayoría de situaciones",
    onboarding_level_title_v2: "¿Qué puedes hacer en {{language}}?",
    onboarding_level_question: "¿Qué puedes hacer en {{language}}?",
    language_fr: "francés",
    language_en: "inglés",
    language_es: "español",
    onboarding_level_intermediate_description: "Habilidad oral para Intermedio",
    onboarding_level_advanced_description: "Habilidad oral para Avanzado",
    onboarding_adaptation_title: "Así es cómo me adaptaré para ti",
    onboarding_adaptation_beginner: "Dado que elegiste **{{level}}**, hablaré principalmente en {{nativeLanguage}} e introduciré gradualmente palabras y frases en {{targetLanguage}}. Me enfocaré en desarrollar tu vocabulario y oraciones básicas.",
    onboarding_adaptation_intermediate: "Dado que elegiste **{{level}}**, hablaré principalmente en {{targetLanguage}} con explicaciones en {{nativeLanguage}} cuando sea necesario. Te ayudaré a mejorar tu gramática y habilidades conversacionales.",
    onboarding_adaptation_advanced: "Dado que elegiste **{{level}}**, hablaré casi completamente en {{targetLanguage}}. Me enfocaré en refinar tu expresión, modismos y lenguaje profesional.",
    onboarding_adaptation_advanced_tip: "💡 Como aprendiz avanzado, puede que disfrutes del modo Tiempo real — una experiencia de conversación más rápida y sin interrupciones. Puedes activarlo más tarde en Configuración.",
    onboarding_mic_title: "Probemos tu micrófono",
    onboarding_mic_description: "Usaremos tu micrófono para las conversaciones. ¡Asegurémonos de que funcione!",
    onboarding_mic_permission_prompt: "Primero, necesitamos acceso a tu micrófono.",
    onboarding_mic_allow: "Permitir Micrófono",
    onboarding_mic_requesting: "Solicitando acceso al micrófono...",
    onboarding_mic_denied: "Se denegó el acceso al micrófono. Por favor, actívalo en la configuración de tu navegador e inténtalo de nuevo.",
    onboarding_mic_retry: "Intentar de nuevo",
    onboarding_mic_skip: "Omitir este paso",
    onboarding_mic_ready: "¡Tu micrófono está listo! Haz clic en el botón de grabación y di algunas palabras.",
    onboarding_mic_granted: "¡Micrófono listo! Haz clic en el botón de abajo y di algunas palabras.",
    onboarding_mic_tip: "Consejo: Usa auriculares y un lugar tranquilo para mejores resultados.",
    onboarding_mic_recording: "Grabando... Haz clic para detener",
    onboarding_mic_play_back: "¡Genial! Escucha tu grabación:",
    onboarding_mic_success: "¡Tu micrófono funciona perfectamente!",
    onboarding_mic_record_again: "Grabar de nuevo",
    onboarding_conversation_title: "Tu primera conversación",
    onboarding_conversation_start_prompt: "¡Haz clic en el botón verde y di {{greeting}} para comenzar!",
    onboarding_conversation_done: "¡Gran conversación!",
    onboarding_conversation_complete_hint: "Configurando tu plan de aprendizaje personalizado...",
    onboarding_recap_title: "¡Completaste tu primera conversación!",
    onboarding_recap_description: "¡Buen trabajo! Has dado tu primer paso hacia la fluidez en {{language}}.",
    onboarding_recap_accomplished: "Lo que lograste:",
    onboarding_recap_had_conversation: "Tuviste tu primera conversación en {{language}}",
    onboarding_recap_learned_word: "Aprendiste una nueva palabra de vocabulario para tu profesión",
    onboarding_recap_personalized: "Tu plan de aprendizaje está siendo creado basado en tu conversación",
    onboarding_auto_objective: "Aprender {{language}} para uso profesional basado en conversación introductoria",
    onboarding_save_error: "Algo salió mal. Tu progreso fue guardado localmente.",
    onboarding_achievement_title: "¡día de aprendizaje de {{language}} completado!",
    onboarding_achievement_summary: "Tu ruta de aprendizaje personalizada de {{language}} está lista. ¡Continúa tu camino en cualquier momento!",
    onboarding_achievement_continue: "¡Continuar!",
    onboarding_achievement_stop: "Parar por hoy",
  },

  de: {
    // Native Language Step
    onboarding_native_language_title: "Was ist Ihre Muttersprache?",
    onboarding_native_language_description: "Wählen Sie Ihre Muttersprache, um Ihr Erlebnis zu personalisieren.",
    onboarding_more_languages: "Weitere Sprachen",
    onboarding_show_less_languages: "Weniger anzeigen",

    // Target Language Step
    onboarding_target_language_title: "Welche Sprache möchten Sie lernen?",
    onboarding_target_language_description: "Wählen Sie die Sprache, die Sie lernen möchten. Sie hören eine Begrüßung als Vorschau.",

    // Age Step
    onboarding_age_title: "Sind Sie erwachsen?",
    onboarding_age_yes: "Ja",
    onboarding_age_no: "Nein",
    onboarding_age_adult_description: "18 Jahre oder älter",
    onboarding_age_child_description: "Unter 18 Jahren",
    onboarding_age_description: "Dies hilft uns, altersgerechte Inhalte anzubieten.",

    // Level Step (CEFR)
    onboarding_level_title: "Was ist Ihr aktuelles Niveau?",
    onboarding_level_description_cefr: "Wählen Sie Ihr Sprachniveau nach dem GER-Maßstab.",
    onboarding_level_unknown: "Ich weiß es nicht",
    onboarding_level_placement_hint: "Wir ermitteln Ihr Niveau in Ihrem ersten Gespräch.",
    cefr_a1_description: "Absoluter Anfänger",
    cefr_a2_description: "Grundlegende Phrasen und einfache Gespräche",
    cefr_b1_description: "Kann vertraute Themen besprechen",
    cefr_b2_description: "Kann mit Muttersprachlern interagieren",
    cefr_c1_description: "Fließend für sozialen und beruflichen Gebrauch",
    cefr_c2_description: "Fast muttersprachliches Niveau",

    // Pronoun Step
    onboarding_pronoun_title: "Welche Pronomen verwenden Sie?",
    onboarding_pronoun_description: "Einige Sprachen haben grammatisches Geschlecht. Dies hilft uns, Sie korrekt anzusprechen.",
    pronoun_masculine: "Er/Ihm",
    pronoun_feminine: "Sie/Ihr",
    pronoun_nonbinary: "Divers",
    pronoun_unspecified: "Keine Angabe",

    // Terms and Conditions
    terms_checkbox_label: "Ich akzeptiere die",
    terms_and_conditions: "Allgemeinen Geschäftsbedingungen",

    // Completion Step
    onboarding_completion_title: "Alles bereit!",
    onboarding_completion_description: "Hier ist eine Zusammenfassung Ihrer Einstellungen. Sie können diese später in den Einstellungen ändern.",
    start_learning: "Lernen beginnen",
    saving: "Speichern...",
    redirecting: "Weiterleitung...",
    onboarding_privacy_note: "Ihre Einstellungen werden sicher gespeichert und helfen uns, Ihr Lernerlebnis zu personalisieren.",

    // Learning Objective Step
    onboarding_objective_title: "Letzte Frage: Was ist Ihr Lernziel?",
    onboarding_objective_description: 'Teilen Sie Ihr Ziel mit, um uns bei der Personalisierung Ihres Lernpfads zu helfen. Geben Sie einen Zeitrahmen und ein konkretes Beispiel an, wenn möglich.',
    onboarding_objective_placeholder: 'z.B. "Ich möchte in 6 Monaten Kunden auf Französisch präsentieren können..."',
    onboarding_objective_or: "oder",
    onboarding_objective_record: "Aufnehmen",
    onboarding_objective_stop: "Stopp",
    onboarding_objective_start_recording: "Sprachaufnahme starten",
    onboarding_objective_stop_recording: "Aufnahme beenden",
    onboarding_objective_transcribing: "Transkribieren...",
    onboarding_objective_mic_denied: "Mikrofonzugriff verweigert. Bitte erlauben Sie den Mikrofonzugriff und versuchen Sie es erneut.",
    onboarding_objective_transcription_error: "Audio-Transkription fehlgeschlagen. Bitte versuchen Sie es erneut oder geben Sie Ihr Ziel ein.",
    onboarding_objective_skip_hint: "Dieser Schritt ist optional. Sie können ihn überspringen, wenn Sie noch unsicher sind.",
    onboarding_objective_entered: "Sie haben ein Lernziel eingegeben.",
    onboarding_objective_aria_label: "Ihr Lernziel",

    // Notifications Step
    onboarding_notifications_title: "Bleiben Sie mit Übungserinnerungen auf Kurs",
    onboarding_finish: "Fertig",
    onboarding_notifications_description: "Regelmäßiges Üben ist der Schlüssel zum Sprachenlernen. Wir senden Ihnen freundliche Erinnerungen, um Ihnen zu helfen, konsequent zu bleiben.",
    onboarding_notifications_recommendation: "Wir empfehlen kurze Sitzungen von 10-15 Minuten pro Tag. Forschungen von Stanford und Cambridge zeigen, dass kurzes, verteiltes Üben effektiver ist als lange, seltene Sitzungen.",
    onboarding_notifications_audio: "Möchten Sie, dass ich Ihnen Übungserinnerungen sende?",
    onboarding_notifications_yes: "Ja, erinnere mich",
    onboarding_notifications_no: "Nein danke",
    onboarding_notifications_days_label: "Welche Tage?",
    onboarding_notifications_time_label: "Um welche Uhrzeit?",
    onboarding_notifications_email_notice: "Wir senden Erinnerungen an Ihre E-Mail-Adresse.",
    day_mon: "Mo",
    day_tue: "Di",
    day_wed: "Mi",
    day_thu: "Do",
    day_fri: "Fr",
    day_sat: "Sa",
    day_sun: "So",
    notifications_summary_enabled: "Erinnerungen aktiviert für {{count}} Tage um {{time}}",
    notifications_summary_disabled: "Erinnerungen sind deaktiviert",

    // Calendar section
    onboarding_calendar_description: "Oder zu Ihrem Kalender hinzufügen",
    onboarding_calendar_google: "Google Kalender",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: ".ics herunterladen",
    onboarding_calendar_outlook_hint: "Tipp: In Outlook nach dem Hinzufügen als wiederkehrend festlegen",
    onboarding_calendar_added: "Hinzugefügt",

    // Email checkbox
    onboarding_email_checkbox_label: "Erinnerungen per E-Mail senden",

    // Warning
    onboarding_no_reminders_warning: "Keine Erinnerungen eingerichtet. Regelmäßiges Üben hilft Ihnen, schneller zu lernen!",

    // Onboarding step titles
    onboarding_step_native_language: "Muttersprache",
    onboarding_step_target_language: "Sprache zu lernen",
    onboarding_step_age: "Alter",
    onboarding_step_level: "Niveau",
    onboarding_step_pronoun: "Pronomen",
    onboarding_step_objective: "Ziel",
    onboarding_step_notifications: "Erinnerungen",
    onboarding_step_completion: "Abschließen",

    // V2 Steps
    onboarding_welcome_title: "Willkommen!",
    onboarding_welcome_text: "Beginnen wir mit ein paar Fragen und Ihrem ersten arbeitsbezogenen Gespräch.",
    onboarding_welcome_duration: "Die Einrichtung dauert etwa 2 Minuten, dann folgt ein kurzes erstes Gespräch.",
    onboarding_step_ready: "Bereit",
    onboarding_ready_title: "Alles bereit!",
    onboarding_ready_description: "Als Nächstes führen Sie ein kurzes erstes Gespräch, damit ich Sie kennenlernen und Ihnen Ihr erstes Wort beibringen kann.",
    onboarding_ready_learning: "Lernen",
    onboarding_ready_level: "Niveau",
    onboarding_ready_start: "Mein erstes Gespräch starten",
    onboarding_ready_not_now: "Nicht jetzt",
    onboarding_mic_listening: "Ich höre zu… sagen Sie ein paar Wörter",
    onboarding_mic_hear_yes: "Ich höre mich",
    onboarding_mic_hear_no: "Ich höre mich nicht",
    onboarding_mic_troubleshoot: "Keine Sorge — prüfen Sie, ob das richtige Mikrofon ausgewählt und die Lautstärke aufgedreht ist, und nehmen Sie dann erneut auf.",
    onboarding_mic_playback_confirm: "Hören Sie sich Ihre Aufnahme an — können Sie sich hören?",
    onboarding_accept_continue: "Akzeptieren & Weiter",
    onboarding_back: "Zurück",
    onboarding_how_it_works_title: "Wie es funktioniert",
    onboarding_how_it_works_text: "Ich verstehe sowohl Ihre Muttersprache als auch Ihre Zielsprache. Ich verwende beide, um Ihnen beim Lernen zu helfen, aber versuchen Sie, Ihre neue Sprache so viel wie möglich zu verwenden!",
    onboarding_level_beginner_description: "Mündliche Kompetenz für Anfänger",
    level_beginner: "Anfänger",
    level_intermediate: "Mittelstufe",
    level_advanced: "Fortgeschritten",
    level_beginner_description: "Ich kann grundlegende Sätze und Ausdrücke verstehen und verwenden",
    level_intermediate_description: "Ich kann Gespräche über vertraute Themen führen und Erfahrungen beschreiben",
    level_advanced_description: "Ich kann mich fließend und spontan in den meisten Situationen ausdrücken",
    onboarding_level_title_v2: "Was können Sie auf {{language}} tun?",
    onboarding_level_question: "Was können Sie auf {{language}} tun?",
    language_fr: "Französisch",
    language_en: "Englisch",
    language_es: "Spanisch",
    onboarding_level_intermediate_description: "Mündliche Kompetenz für Fortgeschrittene",
    onboarding_level_advanced_description: "Mündliche Kompetenz für Experten",
    onboarding_adaptation_title: "So werde ich mich für Sie anpassen",
    onboarding_adaptation_beginner: "Da Sie **{{level}}** gewählt haben, werde ich hauptsächlich auf {{nativeLanguage}} sprechen und schrittweise Wörter und Sätze auf {{targetLanguage}} einführen. Ich werde mich auf den Aufbau Ihres Vokabulars und einfacher Sätze konzentrieren.",
    onboarding_adaptation_intermediate: "Da Sie **{{level}}** gewählt haben, werde ich hauptsächlich auf {{targetLanguage}} sprechen, mit Erklärungen auf {{nativeLanguage}} wenn nötig. Ich werde Ihnen helfen, Ihre Grammatik und Gesprächsfähigkeiten zu verbessern.",
    onboarding_adaptation_advanced: "Da Sie **{{level}}** gewählt haben, werde ich fast ausschließlich auf {{targetLanguage}} sprechen. Ich werde mich auf die Verfeinerung Ihres Ausdrucks, Redewendungen und Fachsprache konzentrieren.",
    onboarding_adaptation_advanced_tip: "💡 Als fortgeschrittener Lernender könnten Sie den Echtzeit-Modus genießen — ein schnelleres, ununterbrochenes Gesprächserlebnis. Sie können ihn später in den Einstellungen aktivieren.",
    onboarding_mic_title: "Testen wir Ihr Mikrofon",
    onboarding_mic_description: "Wir werden Ihr Mikrofon für Gespräche verwenden. Stellen wir sicher, dass es funktioniert!",
    onboarding_mic_permission_prompt: "Zuerst benötigen wir Zugang zu Ihrem Mikrofon.",
    onboarding_mic_allow: "Mikrofon erlauben",
    onboarding_mic_requesting: "Mikrofonzugriff wird angefordert...",
    onboarding_mic_denied: "Der Zugriff auf das Mikrofon wurde verweigert. Bitte aktivieren Sie es in Ihren Browsereinstellungen und versuchen Sie es erneut.",
    onboarding_mic_retry: "Erneut versuchen",
    onboarding_mic_skip: "Diesen Schritt überspringen",
    onboarding_mic_ready: "Ihr Mikrofon ist bereit! Klicken Sie auf den Aufnahmeknopf und sagen Sie ein paar Wörter.",
    onboarding_mic_granted: "Mikrofon bereit! Klicken Sie auf die Schaltfläche unten und sagen Sie ein paar Wörter.",
    onboarding_mic_tip: "Tipp: Verwenden Sie Kopfhörer und einen ruhigen Ort für beste Ergebnisse.",
    onboarding_mic_recording: "Aufnahme... Klicken Sie zum Stoppen",
    onboarding_mic_play_back: "Super! Hören Sie sich Ihre Aufnahme an:",
    onboarding_mic_success: "Ihr Mikrofon funktioniert perfekt!",
    onboarding_mic_record_again: "Erneut aufnehmen",
    onboarding_conversation_title: "Ihr erstes Gespräch",
    onboarding_conversation_start_prompt: "Klicken Sie auf die grüne Schaltfläche und sagen Sie {{greeting}} um zu beginnen!",
    onboarding_conversation_done: "Tolles Gespräch!",
    onboarding_conversation_complete_hint: "Ihr persönlicher Lernplan wird erstellt...",
    onboarding_recap_title: "Sie haben Ihr erstes Gespräch abgeschlossen!",
    onboarding_recap_description: "Gut gemacht! Sie haben Ihren ersten Schritt zur Beherrschung von {{language}} gemacht.",
    onboarding_recap_accomplished: "Was Sie erreicht haben:",
    onboarding_recap_had_conversation: "Hatten Ihr erstes Gespräch auf {{language}}",
    onboarding_recap_learned_word: "Haben ein neues Vokabular für Ihren Beruf gelernt",
    onboarding_recap_personalized: "Ihr Lernplan wird basierend auf Ihrem Gespräch erstellt",
    onboarding_auto_objective: "{{language}} für den beruflichen Gebrauch basierend auf dem Einführungsgespräch lernen",
    onboarding_save_error: "Etwas ist schiefgelaufen. Ihr Fortschritt wurde lokal gespeichert.",
    onboarding_achievement_title: "Tag des {{language}}-Lernens abgeschlossen!",
    onboarding_achievement_summary: "Ihr persönlicher {{language}}-Lernpfad ist bereit. Setzen Sie Ihre Reise jederzeit fort!",
    onboarding_achievement_continue: "Weiter!",
    onboarding_achievement_stop: "Für heute aufhören",
  },

  it: {
    // Native Language Step
    onboarding_native_language_title: "Qual è la tua lingua madre?",
    onboarding_native_language_description: "Seleziona la tua lingua madre per personalizzare la tua esperienza.",
    onboarding_more_languages: "Altre lingue",
    onboarding_show_less_languages: "Mostra meno",

    // Target Language Step
    onboarding_target_language_title: "Quale lingua vuoi imparare?",
    onboarding_target_language_description: "Scegli la lingua che vuoi imparare. Sentirai un saluto di anteprima.",

    // Age Step
    onboarding_age_title: "Sei un adulto?",
    onboarding_age_yes: "Sì",
    onboarding_age_no: "No",
    onboarding_age_adult_description: "18 anni o più",
    onboarding_age_child_description: "Meno di 18 anni",
    onboarding_age_description: "Questo ci aiuta a fornirti contenuti appropriati per la tua età.",

    // Level Step (CEFR)
    onboarding_level_title: "Qual è il tuo livello attuale?",
    onboarding_level_description_cefr: "Seleziona il tuo livello di competenza usando la scala QCER.",
    onboarding_level_unknown: "Non lo so",
    onboarding_level_placement_hint: "Determineremo il tuo livello nella prima conversazione.",
    cefr_a1_description: "Principiante assoluto",
    cefr_a2_description: "Frasi base e conversazioni semplici",
    cefr_b1_description: "Può discutere argomenti familiari",
    cefr_b2_description: "Può interagire con madrelingua",
    cefr_c1_description: "Fluente per uso sociale e professionale",
    cefr_c2_description: "Livello quasi madrelingua",

    // Pronoun Step
    onboarding_pronoun_title: "Quali sono i tuoi pronomi?",
    onboarding_pronoun_description: "Alcune lingue hanno generi grammaticali. Questo ci aiuta a rivolgerci a te correttamente.",
    pronoun_masculine: "Lui",
    pronoun_feminine: "Lei",
    pronoun_nonbinary: "Loro",
    pronoun_unspecified: "Preferisco non dire",

    // Terms and Conditions
    terms_checkbox_label: "Accetto i",
    terms_and_conditions: "Termini e Condizioni",

    // Completion Step
    onboarding_completion_title: "Tutto pronto!",
    onboarding_completion_description: "Ecco un riepilogo delle tue preferenze. Puoi modificarle in seguito nelle impostazioni.",
    start_learning: "Inizia a imparare",
    saving: "Salvataggio...",
    redirecting: "Reindirizzamento...",
    onboarding_privacy_note: "Le tue preferenze sono memorizzate in modo sicuro e ci aiutano a personalizzare la tua esperienza di apprendimento.",

    // Learning Objective Step
    onboarding_objective_title: "Ultima cosa: Qual è il tuo obiettivo di apprendimento?",
    onboarding_objective_description: 'Condividi il tuo obiettivo per aiutarci a personalizzare il tuo percorso di apprendimento. Includi un arco temporale e un esempio concreto, se possibile.',
    onboarding_objective_placeholder: 'es. "Voglio poter presentare ai clienti in francese entro 6 mesi..."',
    onboarding_objective_or: "oppure",
    onboarding_objective_record: "Registra",
    onboarding_objective_stop: "Ferma",
    onboarding_objective_start_recording: "Avvia registrazione vocale",
    onboarding_objective_stop_recording: "Ferma registrazione",
    onboarding_objective_transcribing: "Trascrizione in corso...",
    onboarding_objective_mic_denied: "Accesso al microfono negato. Per favore, consenti l'accesso al microfono e riprova.",
    onboarding_objective_transcription_error: "Trascrizione audio fallita. Per favore, riprova o digita il tuo obiettivo.",
    onboarding_objective_skip_hint: "Questo passaggio è opzionale. Puoi saltarlo se non sei ancora sicuro.",
    onboarding_objective_entered: "Hai inserito un obiettivo di apprendimento.",
    onboarding_objective_aria_label: "Il tuo obiettivo di apprendimento",

    // Notifications Step
    onboarding_notifications_title: "Rimani in pista con i promemoria di pratica",
    onboarding_finish: "Fatto",
    onboarding_notifications_description: "La pratica regolare è la chiave per imparare le lingue. Ti invieremo promemoria amichevoli per aiutarti a rimanere costante.",
    onboarding_notifications_recommendation: "Consigliamo brevi sessioni di 10-15 minuti ogni giorno. Ricerche di Stanford e Cambridge dimostrano che la pratica breve e distanziata è più efficace di sessioni lunghe e poco frequenti.",
    onboarding_notifications_audio: "Vorresti che ti inviassi dei promemoria di pratica?",
    onboarding_notifications_yes: "Sì, ricordamelo",
    onboarding_notifications_no: "No grazie",
    onboarding_notifications_days_label: "Quali giorni?",
    onboarding_notifications_time_label: "A che ora?",
    onboarding_notifications_email_notice: "Invieremo i promemoria al tuo indirizzo email.",
    day_mon: "Lun",
    day_tue: "Mar",
    day_wed: "Mer",
    day_thu: "Gio",
    day_fri: "Ven",
    day_sat: "Sab",
    day_sun: "Dom",
    notifications_summary_enabled: "Promemoria attivati per {{count}} giorni alle {{time}}",
    notifications_summary_disabled: "I promemoria sono disattivati",

    // Calendar section
    onboarding_calendar_description: "Oppure aggiungi al tuo calendario",
    onboarding_calendar_google: "Google Calendar",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: "Scarica .ics",
    onboarding_calendar_outlook_hint: "Suggerimento: Imposta come ricorrente in Outlook dopo l'aggiunta",
    onboarding_calendar_added: "Aggiunto",

    // Email checkbox
    onboarding_email_checkbox_label: "Inviami promemoria via email",

    // Warning
    onboarding_no_reminders_warning: "Nessun promemoria impostato. La pratica costante ti aiuta a imparare più velocemente!",

    // Onboarding step titles
    onboarding_step_native_language: "Lingua madre",
    onboarding_step_target_language: "Lingua da imparare",
    onboarding_step_age: "Età",
    onboarding_step_level: "Livello",
    onboarding_step_pronoun: "Pronomi",
    onboarding_step_objective: "Obiettivo",
    onboarding_step_notifications: "Promemoria",
    onboarding_step_completion: "Completare",

    // V2 Steps
    onboarding_welcome_title: "Benvenuto!",
    onboarding_welcome_text: "Iniziamo con qualche domanda e la tua prima conversazione legata al lavoro.",
    onboarding_welcome_duration: "La configurazione richiede circa 2 minuti, poi una breve prima conversazione.",
    onboarding_step_ready: "Pronto",
    onboarding_ready_title: "Tutto pronto!",
    onboarding_ready_description: "Poi avrai una breve prima conversazione così potrò conoscerti e insegnarti la tua prima parola.",
    onboarding_ready_learning: "Apprendimento",
    onboarding_ready_level: "Livello",
    onboarding_ready_start: "Inizia la mia prima conversazione",
    onboarding_ready_not_now: "Non ora",
    onboarding_mic_listening: "Sto ascoltando… di' qualche parola",
    onboarding_mic_hear_yes: "Mi sento",
    onboarding_mic_hear_no: "Non mi sento",
    onboarding_mic_troubleshoot: "Nessun problema — controlla che sia selezionato il microfono giusto e che il volume sia alto, poi registra di nuovo.",
    onboarding_mic_playback_confirm: "Ascolta la tua registrazione — riesci a sentirti?",
    onboarding_accept_continue: "Accetta e continua",
    onboarding_back: "Indietro",
    onboarding_how_it_works_title: "Come funziona",
    onboarding_how_it_works_text: "Capisco sia la tua lingua madre che quella che stai imparando. Userò entrambe per aiutarti, ma cerca di usare la tua nuova lingua il più possibile!",
    onboarding_level_beginner_description: "Competenza orale per Principiante",
    level_beginner: "Principiante",
    level_intermediate: "Intermedio",
    level_advanced: "Avanzato",
    level_beginner_description: "Posso capire e usare frasi ed espressioni di base",
    level_intermediate_description: "Posso avere conversazioni su argomenti familiari e descrivere esperienze",
    level_advanced_description: "Posso esprimermi in modo fluente e spontaneo nella maggior parte delle situazioni",
    onboarding_level_title_v2: "Cosa sei in grado di fare in {{language}}?",
    onboarding_level_question: "Cosa sei in grado di fare in {{language}}?",
    language_fr: "francese",
    language_en: "inglese",
    language_es: "spagnolo",
    onboarding_level_intermediate_description: "Competenza orale per Intermedio",
    onboarding_level_advanced_description: "Competenza orale per Avanzato",
    onboarding_adaptation_title: "Ecco come mi adatterò per te",
    onboarding_adaptation_beginner: "Dato che hai scelto **{{level}}**, parlerò principalmente in {{nativeLanguage}} e introdurrò gradualmente parole e frasi in {{targetLanguage}}. Mi concentrerò sul costruire il tuo vocabolario e le frasi di base.",
    onboarding_adaptation_intermediate: "Dato che hai scelto **{{level}}**, parlerò principalmente in {{targetLanguage}} con spiegazioni in {{nativeLanguage}} quando necessario. Ti aiuterò a migliorare la tua grammatica e le capacità conversazionali.",
    onboarding_adaptation_advanced: "Dato che hai scelto **{{level}}**, parlerò quasi interamente in {{targetLanguage}}. Mi concentrerò sul raffinare la tua espressione, gli idiomi e il linguaggio professionale.",
    onboarding_adaptation_advanced_tip: "💡 Come studente avanzato, potresti apprezzare la modalità Tempo reale — un'esperienza di conversazione più veloce e senza interruzioni. Puoi attivarla più tardi nelle Impostazioni.",
    onboarding_mic_title: "Testiamo il tuo microfono",
    onboarding_mic_description: "Useremo il tuo microfono per le conversazioni. Assicuriamoci che funzioni!",
    onboarding_mic_permission_prompt: "Prima, abbiamo bisogno dell'accesso al tuo microfono.",
    onboarding_mic_allow: "Consenti Microfono",
    onboarding_mic_requesting: "Richiesta accesso al microfono...",
    onboarding_mic_denied: "L'accesso al microfono è stato negato. Abilitalo nelle impostazioni del browser e riprova.",
    onboarding_mic_retry: "Riprova",
    onboarding_mic_skip: "Salta questo passaggio",
    onboarding_mic_ready: "Il tuo microfono è pronto! Clicca il pulsante di registrazione e di' alcune parole.",
    onboarding_mic_granted: "Microfono pronto! Clicca il pulsante qui sotto e di' alcune parole.",
    onboarding_mic_tip: "Suggerimento: Usa le cuffie e un posto tranquillo per risultati migliori.",
    onboarding_mic_recording: "Registrazione... Clicca per fermare",
    onboarding_mic_play_back: "Ottimo! Ascolta la tua registrazione:",
    onboarding_mic_success: "Il tuo microfono funziona perfettamente!",
    onboarding_mic_record_again: "Registra di nuovo",
    onboarding_conversation_title: "La tua prima conversazione",
    onboarding_conversation_start_prompt: "Clicca il pulsante verde e di' {{greeting}} per iniziare!",
    onboarding_conversation_done: "Ottima conversazione!",
    onboarding_conversation_complete_hint: "Configurazione del tuo piano di apprendimento personalizzato...",
    onboarding_recap_title: "Hai completato la tua prima conversazione!",
    onboarding_recap_description: "Ottimo lavoro! Hai fatto il tuo primo passo verso la fluidità in {{language}}.",
    onboarding_recap_accomplished: "Cosa hai realizzato:",
    onboarding_recap_had_conversation: "Hai avuto la tua prima conversazione in {{language}}",
    onboarding_recap_learned_word: "Hai imparato una nuova parola di vocabolario per la tua professione",
    onboarding_recap_personalized: "Il tuo piano di apprendimento viene creato sulla base della tua conversazione",
    onboarding_auto_objective: "Imparare {{language}} per uso professionale basato sulla conversazione introduttiva",
    onboarding_save_error: "Qualcosa è andato storto. I tuoi progressi sono stati salvati localmente.",
    onboarding_achievement_title: "giorno di apprendimento del {{language}} completato!",
    onboarding_achievement_summary: "Il tuo percorso di apprendimento personalizzato in {{language}} è pronto. Continua il tuo viaggio in qualsiasi momento!",
    onboarding_achievement_continue: "Continua!",
    onboarding_achievement_stop: "Fermati per oggi",
  },

  pt: {
    // Native Language Step
    onboarding_native_language_title: "Qual é a sua língua materna?",
    onboarding_native_language_description: "Selecione sua língua materna para personalizar sua experiência.",
    onboarding_more_languages: "Mais idiomas",
    onboarding_show_less_languages: "Menos idiomas",

    // Target Language Step
    onboarding_target_language_title: "Qual idioma você quer aprender?",
    onboarding_target_language_description: "Escolha o idioma que deseja aprender. Você ouvirá uma saudação de amostra.",

    // Age Step
    onboarding_age_title: "Você é adulto?",
    onboarding_age_yes: "Sim",
    onboarding_age_no: "Não",
    onboarding_age_adult_description: "18 anos ou mais",
    onboarding_age_child_description: "Menos de 18 anos",
    onboarding_age_description: "Isso nos ajuda a fornecer conteúdo apropriado para sua idade.",

    // Level Step (CEFR)
    onboarding_level_title: "Qual é o seu nível atual?",
    onboarding_level_description_cefr: "Selecione seu nível de proficiência usando a escala QECR.",
    onboarding_level_unknown: "Não sei",
    onboarding_level_placement_hint: "Determinaremos seu nível na primeira conversa.",
    cefr_a1_description: "Iniciante completo",
    cefr_a2_description: "Frases básicas e conversas simples",
    cefr_b1_description: "Pode discutir tópicos familiares",
    cefr_b2_description: "Pode interagir com falantes nativos",
    cefr_c1_description: "Fluente para uso social e profissional",
    cefr_c2_description: "Nível quase nativo",

    // Pronoun Step
    onboarding_pronoun_title: "Quais são seus pronomes?",
    onboarding_pronoun_description: "Algumas línguas têm gênero. Isso nos ajuda a falar com você corretamente.",
    pronoun_masculine: "Ele",
    pronoun_feminine: "Ela",
    pronoun_nonbinary: "Elu",
    pronoun_unspecified: "Prefiro não dizer",

    // Terms and Conditions
    terms_checkbox_label: "Eu aceito os",
    terms_and_conditions: "Termos e Condições",

    // Completion Step
    onboarding_completion_title: "Tudo pronto!",
    onboarding_completion_description: "Aqui está um resumo das suas preferências. Você pode alterá-las depois nas configurações.",
    start_learning: "Começar a aprender",
    saving: "Salvando...",
    redirecting: "Redirecionando...",
    onboarding_privacy_note: "Suas preferências são armazenadas com segurança e nos ajudam a personalizar sua experiência de aprendizado.",

    // Learning Objective Step
    onboarding_objective_title: "Última coisa: Qual é o seu objetivo de aprendizagem?",
    onboarding_objective_description: 'Compartilhe seu objetivo para nos ajudar a personalizar seu caminho de aprendizagem. Inclua um prazo e um exemplo concreto, como: "Quero poder iniciar uma reunião em inglês em 3 meses."',
    onboarding_objective_placeholder: 'ex. "Quero poder apresentar para clientes em francês em 6 meses..."',
    onboarding_objective_or: "ou",
    onboarding_objective_record: "Gravar",
    onboarding_objective_stop: "Parar",
    onboarding_objective_start_recording: "Iniciar gravação de voz",
    onboarding_objective_stop_recording: "Parar gravação",
    onboarding_objective_transcribing: "Transcrevendo...",
    onboarding_objective_mic_denied: "Acesso ao microfone negado. Por favor, permita o acesso ao microfone e tente novamente.",
    onboarding_objective_transcription_error: "Falha na transcrição do áudio. Por favor, tente novamente ou digite seu objetivo.",
    onboarding_objective_skip_hint: "Este passo é opcional. Você pode pular se ainda não tiver certeza.",
    onboarding_objective_entered: "Você inseriu um objetivo de aprendizagem.",
    onboarding_objective_aria_label: "Seu objetivo de aprendizagem",

    // Notifications Step
    onboarding_notifications_title: "Mantenha-se no caminho com lembretes de prática",
    onboarding_finish: "Concluído",
    onboarding_notifications_description: "A prática regular é fundamental para aprender idiomas. Enviaremos lembretes amigáveis para ajudá-lo a manter a constância.",
    onboarding_notifications_recommendation: "Recomendamos sessões curtas de 10 a 15 minutos todos os dias. Pesquisas de Stanford e Cambridge mostram que a prática curta e espaçada é mais eficaz do que sessões longas e pouco frequentes.",
    onboarding_notifications_audio: "Gostaria que eu enviasse lembretes de prática?",
    onboarding_notifications_yes: "Sim, lembre-me",
    onboarding_notifications_no: "Não, obrigado",
    onboarding_notifications_days_label: "Quais dias?",
    onboarding_notifications_time_label: "Que horas?",
    onboarding_notifications_email_notice: "Enviaremos lembretes para o seu endereço de e-mail.",
    day_mon: "Seg",
    day_tue: "Ter",
    day_wed: "Qua",
    day_thu: "Qui",
    day_fri: "Sex",
    day_sat: "Sáb",
    day_sun: "Dom",
    notifications_summary_enabled: "Lembretes ativados para {{count}} dias às {{time}}",
    notifications_summary_disabled: "Os lembretes estão desativados",

    // Calendar section
    onboarding_calendar_description: "Ou adicione ao seu calendário",
    onboarding_calendar_google: "Google Agenda",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: "Baixar .ics",
    onboarding_calendar_outlook_hint: "Dica: Configure como recorrente no Outlook após adicionar",
    onboarding_calendar_added: "Adicionado",

    // Email checkbox
    onboarding_email_checkbox_label: "Enviar lembretes por e-mail",

    // Warning
    onboarding_no_reminders_warning: "Nenhum lembrete configurado. A prática constante ajuda você a aprender mais rápido!",

    // Onboarding step titles
    onboarding_step_native_language: "Língua materna",
    onboarding_step_target_language: "Língua a aprender",
    onboarding_step_age: "Idade",
    onboarding_step_level: "Nível",
    onboarding_step_pronoun: "Pronomes",
    onboarding_step_objective: "Objetivo",
    onboarding_step_notifications: "Lembretes",
    onboarding_step_completion: "Completar",

    // V2 Steps
    onboarding_welcome_title: "Bem-vindo!",
    onboarding_welcome_text: "Vamos começar com algumas perguntas e sua primeira conversa relacionada ao trabalho.",
    onboarding_welcome_duration: "A configuração leva cerca de 2 minutos, depois uma breve primeira conversa.",
    onboarding_step_ready: "Pronto",
    onboarding_ready_title: "Tudo pronto!",
    onboarding_ready_description: "A seguir, você terá uma breve primeira conversa para que eu possa conhecê-lo e ensinar sua primeira palavra.",
    onboarding_ready_learning: "Aprendendo",
    onboarding_ready_level: "Nível",
    onboarding_ready_start: "Iniciar minha primeira conversa",
    onboarding_ready_not_now: "Agora não",
    onboarding_mic_listening: "Ouvindo… diga algumas palavras",
    onboarding_mic_hear_yes: "Consigo me ouvir",
    onboarding_mic_hear_no: "Não consigo me ouvir",
    onboarding_mic_troubleshoot: "Não se preocupe — verifique se o microfone correto está selecionado e se o volume está alto, depois grave novamente.",
    onboarding_mic_playback_confirm: "Ouça sua gravação — você consegue se ouvir?",
    onboarding_accept_continue: "Aceitar e continuar",
    onboarding_back: "Voltar",
    onboarding_how_it_works_title: "Como funciona",
    onboarding_how_it_works_text: "Eu entendo tanto o seu idioma nativo quanto o que você está aprendendo. Vou usar os dois para te ajudar, mas tente usar o seu novo idioma o máximo possível!",
    onboarding_level_beginner_description: "Habilidade oral para Iniciante",
    level_beginner: "Iniciante",
    level_intermediate: "Intermediário",
    level_advanced: "Avançado",
    level_beginner_description: "Consigo entender e usar frases e expressões básicas",
    level_intermediate_description: "Consigo ter conversas sobre temas familiares e descrever experiências",
    level_advanced_description: "Consigo me expressar fluentemente e espontaneamente na maioria das situações",
    onboarding_level_title_v2: "O que você consegue fazer em {{language}}?",
    onboarding_level_question: "O que você consegue fazer em {{language}}?",
    language_fr: "francês",
    language_en: "inglês",
    language_es: "espanhol",
    onboarding_level_intermediate_description: "Habilidade oral para Intermediário",
    onboarding_level_advanced_description: "Habilidade oral para Avançado",
    onboarding_adaptation_title: "Veja como vou me adaptar para você",
    onboarding_adaptation_beginner: "Como você escolheu **{{level}}**, vou falar principalmente em {{nativeLanguage}} e introduzir gradualmente palavras e frases em {{targetLanguage}}. Vou focar no desenvolvimento do seu vocabulário e frases básicas.",
    onboarding_adaptation_intermediate: "Como você escolheu **{{level}}**, vou falar principalmente em {{targetLanguage}} com explicações em {{nativeLanguage}} quando necessário. Vou ajudá-lo a melhorar sua gramática e habilidades de conversação.",
    onboarding_adaptation_advanced: "Como você escolheu **{{level}}**, vou falar quase inteiramente em {{targetLanguage}}. Vou focar em refinar sua expressão, expressões idiomáticas e linguagem profissional.",
    onboarding_adaptation_advanced_tip: "💡 Como aprendiz avançado, você pode gostar do modo Tempo real — uma experiência de conversa mais rápida e sem interrupções. Você pode ativá-lo mais tarde em Configurações.",
    onboarding_mic_title: "Vamos testar seu microfone",
    onboarding_mic_description: "Vamos usar seu microfone para conversas. Vamos garantir que funcione!",
    onboarding_mic_permission_prompt: "Primeiro, precisamos de acesso ao seu microfone.",
    onboarding_mic_allow: "Permitir Microfone",
    onboarding_mic_requesting: "Solicitando acesso ao microfone...",
    onboarding_mic_denied: "O acesso ao microfone foi negado. Por favor, ative-o nas configurações do navegador e tente novamente.",
    onboarding_mic_retry: "Tentar novamente",
    onboarding_mic_skip: "Pular esta etapa",
    onboarding_mic_ready: "Seu microfone está pronto! Clique no botão de gravação e diga algumas palavras.",
    onboarding_mic_granted: "Microfone pronto! Clique no botão abaixo e diga algumas palavras.",
    onboarding_mic_tip: "Dica: Use fones de ouvido e um lugar silencioso para melhores resultados.",
    onboarding_mic_recording: "Gravando... Clique para parar",
    onboarding_mic_play_back: "Ótimo! Ouça sua gravação:",
    onboarding_mic_success: "Seu microfone está funcionando perfeitamente!",
    onboarding_mic_record_again: "Gravar novamente",
    onboarding_conversation_title: "Sua primeira conversa",
    onboarding_conversation_start_prompt: "Clique no botão verde e diga {{greeting}} para começar!",
    onboarding_conversation_done: "Ótima conversa!",
    onboarding_conversation_complete_hint: "Configurando seu plano de aprendizado personalizado...",
    onboarding_recap_title: "Você concluiu sua primeira conversa!",
    onboarding_recap_description: "Muito bem! Você deu seu primeiro passo em direção à fluência em {{language}}.",
    onboarding_recap_accomplished: "O que você realizou:",
    onboarding_recap_had_conversation: "Teve sua primeira conversa em {{language}}",
    onboarding_recap_learned_word: "Aprendeu uma nova palavra de vocabulário para sua profissão",
    onboarding_recap_personalized: "Seu plano de aprendizado está sendo criado com base na sua conversa",
    onboarding_auto_objective: "Aprender {{language}} para uso profissional com base na conversa introdutória",
    onboarding_save_error: "Algo deu errado. Seu progresso foi salvo localmente.",
    onboarding_achievement_title: "dia de aprendizado de {{language}} conquistado!",
    onboarding_achievement_summary: "Seu caminho de aprendizado personalizado em {{language}} está pronto. Continue sua jornada a qualquer momento!",
    onboarding_achievement_continue: "Continuar!",
    onboarding_achievement_stop: "Parar por hoje",
  },

  zh: {
    // Native Language Step
    onboarding_native_language_title: "您的母语是什么？",
    onboarding_native_language_description: "选择您的母语以个性化您的体验。",
    onboarding_more_languages: "更多语言",
    onboarding_show_less_languages: "收起",

    // Target Language Step
    onboarding_target_language_title: "您想学习哪种语言？",
    onboarding_target_language_description: "选择您想学习的语言。您将听到欢迎问候。",

    // Age Step
    onboarding_age_title: "您是成年人吗？",
    onboarding_age_yes: "是",
    onboarding_age_no: "否",
    onboarding_age_adult_description: "18岁或以上",
    onboarding_age_child_description: "18岁以下",
    onboarding_age_description: "这有助于我们提供适合您年龄的内容。",

    // Level Step (CEFR)
    onboarding_level_title: "您目前的水平是什么？",
    onboarding_level_description_cefr: "使用CEFR等级选择您的语言水平。",
    onboarding_level_unknown: "我不知道",
    onboarding_level_placement_hint: "我们将在您的第一次对话中确定您的水平。",
    cefr_a1_description: "完全初学者",
    cefr_a2_description: "基础短语和简单对话",
    cefr_b1_description: "能讨论熟悉的话题",
    cefr_b2_description: "能与母语者交流",
    cefr_c1_description: "社交和职业流利",
    cefr_c2_description: "接近母语水平",

    // Pronoun Step
    onboarding_pronoun_title: "您使用什么代词？",
    onboarding_pronoun_description: "某些语言有语法性别。这有助于我们正确地称呼您。",
    pronoun_masculine: "他",
    pronoun_feminine: "她",
    pronoun_nonbinary: "他们",
    pronoun_unspecified: "不愿透露",

    // Terms and Conditions
    terms_checkbox_label: "我同意",
    terms_and_conditions: "条款和条件",

    // Completion Step
    onboarding_completion_title: "一切就绪！",
    onboarding_completion_description: "以下是您的偏好设置摘要。您可以稍后在设置中更改。",
    start_learning: "开始学习",
    saving: "保存中...",
    redirecting: "跳转中...",
    onboarding_privacy_note: "您的偏好设置已安全存储，帮助我们为您提供个性化的学习体验。",

    // Learning Objective Step
    onboarding_objective_title: "最后一步：您的学习目标是什么？",
    onboarding_objective_description: '分享您的目标，帮助我们为您量身定制学习路径。请包括时间框架和具体示例，例如："我希望在3个月内能够用英语主持会议。"',
    onboarding_objective_placeholder: '例如："我希望在6个月内能够用法语向客户做演示..."',
    onboarding_objective_or: "或者",
    onboarding_objective_record: "录音",
    onboarding_objective_stop: "停止",
    onboarding_objective_start_recording: "开始语音录制",
    onboarding_objective_stop_recording: "停止录制",
    onboarding_objective_transcribing: "正在转录...",
    onboarding_objective_mic_denied: "麦克风访问被拒绝。请允许麦克风访问后重试。",
    onboarding_objective_transcription_error: "音频转录失败。请重试或输入您的目标。",
    onboarding_objective_skip_hint: "此步骤是可选的。如果您还不确定，可以跳过。",
    onboarding_objective_entered: "您已输入学习目标。",
    onboarding_objective_aria_label: "您的学习目标",

    // Notifications Step
    onboarding_notifications_title: "通过练习提醒保持进度",
    onboarding_finish: "完成",
    onboarding_notifications_description: "定期练习是语言学习的关键。我们会发送友好的提醒帮助您保持一致性。",
    onboarding_notifications_recommendation: "我们建议每天进行10-15分钟的短时练习。斯坦福大学和剑桥大学的研究表明，短时间隔练习比长时间低频练习更有效。",
    onboarding_notifications_audio: "您希望我发送练习提醒吗？",
    onboarding_notifications_yes: "是的，提醒我",
    onboarding_notifications_no: "不用了，谢谢",
    onboarding_notifications_days_label: "哪几天？",
    onboarding_notifications_time_label: "什么时间？",
    onboarding_notifications_email_notice: "我们会将提醒发送到您的电子邮箱。",
    day_mon: "周一",
    day_tue: "周二",
    day_wed: "周三",
    day_thu: "周四",
    day_fri: "周五",
    day_sat: "周六",
    day_sun: "周日",
    notifications_summary_enabled: "已启用{{count}}天的提醒，时间为{{time}}",
    notifications_summary_disabled: "提醒已禁用",

    // Calendar section
    onboarding_calendar_description: "或添加到您的日历",
    onboarding_calendar_google: "Google日历",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: "下载 .ics",
    onboarding_calendar_outlook_hint: "提示：添加后在Outlook中设置为重复",
    onboarding_calendar_added: "已添加",

    // Email checkbox
    onboarding_email_checkbox_label: "发送电子邮件提醒",

    // Warning
    onboarding_no_reminders_warning: "未设置提醒。持续练习可帮助您更快学习！",

    // Onboarding step titles
    onboarding_step_native_language: "母语",
    onboarding_step_target_language: "目标语言",
    onboarding_step_age: "年龄",
    onboarding_step_level: "水平",
    onboarding_step_pronoun: "代词",
    onboarding_step_objective: "目标",
    onboarding_step_notifications: "提醒",
    onboarding_step_completion: "完成",

    // V2 Steps
    onboarding_welcome_title: "欢迎！",
    onboarding_welcome_text: "让我们从几个问题和您的第一次工作相关对话开始。",
    onboarding_welcome_duration: "设置大约需要2分钟，然后是一段简短的初次对话。",
    onboarding_step_ready: "准备就绪",
    onboarding_ready_title: "一切就绪！",
    onboarding_ready_description: "接下来，您将进行一段简短的初次对话，让我了解您并教您第一个单词。",
    onboarding_ready_learning: "学习中",
    onboarding_ready_level: "水平",
    onboarding_ready_start: "开始我的第一次对话",
    onboarding_ready_not_now: "暂不",
    onboarding_mic_listening: "正在聆听……说几个字",
    onboarding_mic_hear_yes: "能听到",
    onboarding_mic_hear_no: "听不到",
    onboarding_mic_troubleshoot: "别担心——请检查是否选择了正确的麦克风并调高音量，然后重新录音。",
    onboarding_mic_playback_confirm: "听一下您的录音——能听到自己的声音吗？",
    onboarding_accept_continue: "接受并继续",
    onboarding_back: "返回",
    onboarding_how_it_works_title: "工作原理",
    onboarding_how_it_works_text: "我能理解你的母语和你正在学习的语言。我会用两种语言来帮助你学习，但请尽量多使用你的新语言！",
    onboarding_level_beginner_description: "初学者的口语能力",
    level_beginner: "初学者",
    level_intermediate: "中级",
    level_advanced: "高级",
    level_beginner_description: "我能理解和使用基本的短语和表达",
    level_intermediate_description: "我能就熟悉的话题进行对话并描述经历",
    level_advanced_description: "我能在大多数情况下流利自如地表达自己",
    onboarding_level_title_v2: "你在{{language}}方面能做什么？",
    onboarding_level_question: "你在{{language}}方面能做什么？",
    language_fr: "法语",
    language_en: "英语",
    language_es: "西班牙语",
    onboarding_level_intermediate_description: "中级学习者的口语能力",
    onboarding_level_advanced_description: "高级学习者的口语能力",
    onboarding_adaptation_title: "以下是我将如何为您调整",
    onboarding_adaptation_beginner: "由于您选择了**{{level}}**，我将主要用{{nativeLanguage}}说话，并逐步引入{{targetLanguage}}的词汇和短语。我将专注于建立您的词汇量和基本句型。",
    onboarding_adaptation_intermediate: "由于您选择了**{{level}}**，我将主要用{{targetLanguage}}说话，在需要时用{{nativeLanguage}}解释。我将帮助您提高语法和对话技能。",
    onboarding_adaptation_advanced: "由于您选择了**{{level}}**，我将几乎完全用{{targetLanguage}}说话。我将专注于完善您的表达、习语和专业语言。",
    onboarding_adaptation_advanced_tip: "💡 作为高级学习者，您可能会喜欢实时模式——更快速、不间断的对话体验。您可以稍后在设置中启用此功能。",
    onboarding_mic_title: "让我们测试您的麦克风",
    onboarding_mic_description: "我们将使用您的麦克风进行对话。确保它正常工作！",
    onboarding_mic_permission_prompt: "首先，我们需要访问您的麦克风。",
    onboarding_mic_allow: "允许麦克风",
    onboarding_mic_requesting: "正在请求麦克风访问权限...",
    onboarding_mic_denied: "麦克风访问被拒绝。请在浏览器设置中启用它，然后重试。",
    onboarding_mic_retry: "重试",
    onboarding_mic_skip: "跳过此步骤",
    onboarding_mic_ready: "您的麦克风已准备好！点击录音按钮说几个字。",
    onboarding_mic_granted: "麦克风已准备好！点击下面的按钮说几个字。",
    onboarding_mic_tip: "提示：使用耳机并在安静的地方以获得最佳效果。",
    onboarding_mic_recording: "录音中... 点击停止",
    onboarding_mic_play_back: "太好了！听听您的录音：",
    onboarding_mic_success: "您的麦克风工作完美！",
    onboarding_mic_record_again: "重新录音",
    onboarding_conversation_title: "您的第一次对话",
    onboarding_conversation_start_prompt: "点击绿色按钮，说 {{greeting}} 开始！",
    onboarding_conversation_done: "精彩的对话！",
    onboarding_conversation_complete_hint: "正在创建您的个性化学习计划...",
    onboarding_recap_title: "您完成了第一次对话！",
    onboarding_recap_description: "做得好！您已迈出通往{{language}}流利的第一步。",
    onboarding_recap_accomplished: "您所取得的成就：",
    onboarding_recap_had_conversation: "进行了第一次{{language}}对话",
    onboarding_recap_learned_word: "学到了一个与您职业相关的新词汇",
    onboarding_recap_personalized: "您的学习计划正在根据您的对话创建中",
    onboarding_auto_objective: "根据入门对话，学习{{language}}用于专业用途",
    onboarding_save_error: "出现了问题。您的进度已本地保存。",
    onboarding_achievement_title: "完成了一天的{{language}}学习！",
    onboarding_achievement_summary: "您的个性化{{language}}学习路径已准备好。随时继续您的旅程！",
    onboarding_achievement_continue: "继续！",
    onboarding_achievement_stop: "今天就到这里",
  },

  ar: {
    // Native Language Step
    onboarding_native_language_title: "ما هي لغتك الأم؟",
    onboarding_native_language_description: "اختر لغتك الأم لتخصيص تجربتك.",
    onboarding_more_languages: "المزيد من اللغات",
    onboarding_show_less_languages: "عرض أقل",

    // Target Language Step
    onboarding_target_language_title: "ما اللغة التي تريد تعلمها؟",
    onboarding_target_language_description: "اختر اللغة التي تريد تعلمها. ستسمع تحية ترحيبية.",

    // Age Step
    onboarding_age_title: "هل أنت بالغ؟",
    onboarding_age_yes: "نعم",
    onboarding_age_no: "لا",
    onboarding_age_adult_description: "18 سنة أو أكثر",
    onboarding_age_child_description: "أقل من 18 سنة",
    onboarding_age_description: "هذا يساعدنا على تقديم محتوى مناسب لعمرك.",

    // Level Step (CEFR)
    onboarding_level_title: "ما هو مستواك الحالي؟",
    onboarding_level_description_cefr: "اختر مستوى كفاءتك باستخدام مقياس CEFR.",
    onboarding_level_unknown: "لا أعرف",
    onboarding_level_placement_hint: "سنحدد مستواك في محادثتك الأولى.",
    cefr_a1_description: "مبتدئ تماماً",
    cefr_a2_description: "عبارات أساسية ومحادثات بسيطة",
    cefr_b1_description: "يمكن مناقشة مواضيع مألوفة",
    cefr_b2_description: "يمكن التفاعل مع المتحدثين الأصليين",
    cefr_c1_description: "طلاقة في الاستخدام الاجتماعي والمهني",
    cefr_c2_description: "مستوى قريب من المتحدث الأصلي",

    // Pronoun Step
    onboarding_pronoun_title: "ما هي ضمائرك؟",
    onboarding_pronoun_description: "بعض اللغات لها جنس نحوي. هذا يساعدنا على مخاطبتك بشكل صحيح.",
    pronoun_masculine: "هو",
    pronoun_feminine: "هي",
    pronoun_nonbinary: "هم",
    pronoun_unspecified: "أفضل عدم القول",

    // Terms and Conditions
    terms_checkbox_label: "أوافق على",
    terms_and_conditions: "الشروط والأحكام",

    // Completion Step
    onboarding_completion_title: "أنت جاهز!",
    onboarding_completion_description: "إليك ملخص تفضيلاتك. يمكنك تغييرها لاحقاً في الإعدادات.",
    start_learning: "ابدأ التعلم",
    saving: "جاري الحفظ...",
    redirecting: "جاري التحويل...",
    onboarding_privacy_note: "تُخزن تفضيلاتك بشكل آمن وتساعدنا على تخصيص تجربة التعلم الخاصة بك.",

    // Learning Objective Step
    onboarding_objective_title: "آخر شيء: ما هو هدفك التعليمي؟",
    onboarding_objective_description: "شارك هدفك لمساعدتنا في تخصيص مسار تعلمك. قم بتضمين إطار زمني ومثال ملموس، مثل: \"أريد أن أتمكن من بدء اجتماع باللغة الإنجليزية خلال 3 أشهر.\"",
    onboarding_objective_placeholder: "مثال: \"أريد أن أتمكن من تقديم عروض للعملاء بالفرنسية في غضون 6 أشهر...\"",
    onboarding_objective_or: "أو",
    onboarding_objective_record: "تسجيل",
    onboarding_objective_stop: "إيقاف",
    onboarding_objective_start_recording: "بدء التسجيل الصوتي",
    onboarding_objective_stop_recording: "إيقاف التسجيل",
    onboarding_objective_transcribing: "جاري النسخ...",
    onboarding_objective_mic_denied: "تم رفض الوصول إلى الميكروفون. يرجى السماح بالوصول إلى الميكروفون والمحاولة مرة أخرى.",
    onboarding_objective_transcription_error: "فشل نسخ الصوت. يرجى المحاولة مرة أخرى أو كتابة هدفك.",
    onboarding_objective_skip_hint: "هذه الخطوة اختيارية. يمكنك تخطيها إذا لم تكن متأكداً بعد.",
    onboarding_objective_entered: "لقد أدخلت هدفاً تعليمياً.",
    onboarding_objective_aria_label: "هدفك التعليمي",

    // Notifications Step
    onboarding_notifications_title: "ابق على المسار الصحيح مع تذكيرات الممارسة",
    onboarding_finish: "تم",
    onboarding_notifications_description: "الممارسة المنتظمة هي مفتاح تعلم اللغات. سنرسل لك تذكيرات ودية لمساعدتك على البقاء منتظماً.",
    onboarding_notifications_recommendation: "نوصي بجلسات قصيرة من 10 إلى 15 دقيقة كل يوم. تُظهر أبحاث من ستانفورد وكامبريدج أن الممارسة القصيرة والمتباعدة أكثر فعالية من الجلسات الطويلة وغير المتكررة.",
    onboarding_notifications_audio: "هل تريد أن أرسل لك تذكيرات للممارسة؟",
    onboarding_notifications_yes: "نعم، ذكرني",
    onboarding_notifications_no: "لا، شكراً",
    onboarding_notifications_days_label: "أي أيام؟",
    onboarding_notifications_time_label: "في أي وقت؟",
    onboarding_notifications_email_notice: "سنرسل التذكيرات إلى عنوان بريدك الإلكتروني.",
    day_mon: "الإثنين",
    day_tue: "الثلاثاء",
    day_wed: "الأربعاء",
    day_thu: "الخميس",
    day_fri: "الجمعة",
    day_sat: "السبت",
    day_sun: "الأحد",
    notifications_summary_enabled: "التذكيرات مفعلة لـ {{count}} أيام في الساعة {{time}}",
    notifications_summary_disabled: "التذكيرات معطلة",

    // Calendar section
    onboarding_calendar_description: "أو أضف إلى تقويمك",
    onboarding_calendar_google: "تقويم Google",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: "تحميل .ics",
    onboarding_calendar_outlook_hint: "نصيحة: اضبطه كمتكرر في Outlook بعد الإضافة",
    onboarding_calendar_added: "تمت الإضافة",

    // Email checkbox
    onboarding_email_checkbox_label: "أرسل لي تذكيرات بالبريد الإلكتروني",

    // Warning
    onboarding_no_reminders_warning: "لم يتم تعيين تذكيرات. الممارسة المنتظمة تساعدك على التعلم بشكل أسرع!",

    // Onboarding step titles
    onboarding_step_native_language: "لغتك الأم",
    onboarding_step_target_language: "اللغة التي تريد تعلمها",
    onboarding_step_age: "العمر",
    onboarding_step_level: "المستوى",
    onboarding_step_pronoun: "الضمائر",
    onboarding_step_objective: "الهدف",
    onboarding_step_notifications: "التذكيرات",
    onboarding_step_completion: "إنهاء",

    // V2 Steps
    onboarding_welcome_title: "مرحباً!",
    onboarding_welcome_text: "لنبدأ ببعض الأسئلة ومحادثتك الأولى المتعلقة بالعمل.",
    onboarding_welcome_duration: "يستغرق الإعداد حوالي دقيقتين، ثم محادثة أولى قصيرة.",
    onboarding_step_ready: "جاهز",
    onboarding_ready_title: "أنت جاهز!",
    onboarding_ready_description: "بعد ذلك، ستجري محادثة أولى قصيرة حتى أتعرف عليك وأعلمك كلمتك الأولى.",
    onboarding_ready_learning: "التعلم",
    onboarding_ready_level: "المستوى",
    onboarding_ready_start: "ابدأ محادثتي الأولى",
    onboarding_ready_not_now: "ليس الآن",
    onboarding_mic_listening: "أستمع… قل بضع كلمات",
    onboarding_mic_hear_yes: "أسمع صوتي",
    onboarding_mic_hear_no: "لا أسمع صوتي",
    onboarding_mic_troubleshoot: "لا تقلق — تأكد من اختيار الميكروفون الصحيح ورفع مستوى الصوت، ثم سجل مرة أخرى.",
    onboarding_mic_playback_confirm: "استمع إلى تسجيلك — هل تسمع صوتك؟",
    onboarding_accept_continue: "قبول ومتابعة",
    onboarding_back: "رجوع",
    onboarding_how_it_works_title: "كيف يعمل",
    onboarding_how_it_works_text: "أفهم لغتك الأم واللغة التي تتعلمها. سأستخدم كلتيهما لمساعدتك، لكن حاول استخدام لغتك الجديدة قدر الإمكان!",
    onboarding_level_beginner_description: "المهارة الشفهية للمبتدئين",
    level_beginner: "مبتدئ",
    level_intermediate: "متوسط",
    level_advanced: "متقدم",
    level_beginner_description: "أستطيع فهم واستخدام العبارات والتعبيرات الأساسية",
    level_intermediate_description: "أستطيع إجراء محادثات حول مواضيع مألوفة ووصف التجارب",
    level_advanced_description: "أستطيع التعبير عن نفسي بطلاقة وعفوية في معظم المواقف",
    onboarding_level_title_v2: "ماذا يمكنك فعله بـ{{language}}؟",
    onboarding_level_question: "ماذا يمكنك فعله بـ{{language}}؟",
    language_fr: "الفرنسية",
    language_en: "الإنجليزية",
    language_es: "الإسبانية",
    onboarding_level_intermediate_description: "المهارة الشفهية للمستوى المتوسط",
    onboarding_level_advanced_description: "المهارة الشفهية للمستوى المتقدم",
    onboarding_adaptation_title: "هكذا سأتكيف من أجلك",
    onboarding_adaptation_beginner: "بما أنك اخترت **{{level}}**، سأتحدث بشكل رئيسي بـ {{nativeLanguage}} وأدرج تدريجياً كلمات وجمل بـ {{targetLanguage}}. سأركز على بناء مفرداتك والجمل الأساسية.",
    onboarding_adaptation_intermediate: "بما أنك اخترت **{{level}}**، سأتحدث بشكل رئيسي بـ {{targetLanguage}} مع شرح بـ {{nativeLanguage}} عند الحاجة. سأساعدك على تحسين قواعدك النحوية ومهاراتك في المحادثة.",
    onboarding_adaptation_advanced: "بما أنك اخترت **{{level}}**، سأتحدث بشكل شبه كامل بـ {{targetLanguage}}. سأركز على صقل تعبيرك والتعابير الاصطلاحية واللغة المهنية.",
    onboarding_adaptation_advanced_tip: "💡 بوصفك متعلمًا متقدمًا، قد تستمتع بوضع الوقت الفعلي — تجربة محادثة أسرع وبدون انقطاع. يمكنك تفعيله لاحقًا في الإعدادات.",
    onboarding_mic_title: "لنختبر الميكروفون الخاص بك",
    onboarding_mic_description: "سنستخدم الميكروفون الخاص بك للمحادثات. دعنا نتأكد من أنه يعمل!",
    onboarding_mic_permission_prompt: "أولاً، نحتاج إلى الوصول إلى الميكروفون الخاص بك.",
    onboarding_mic_allow: "السماح بالميكروفون",
    onboarding_mic_requesting: "جارٍ طلب الوصول إلى الميكروفون...",
    onboarding_mic_denied: "تم رفض الوصول إلى الميكروفون. يرجى تمكينه في إعدادات المتصفح والمحاولة مرة أخرى.",
    onboarding_mic_retry: "المحاولة مرة أخرى",
    onboarding_mic_skip: "تخطي هذه الخطوة",
    onboarding_mic_ready: "الميكروفون الخاص بك جاهز! انقر على زر التسجيل وقل بعض الكلمات.",
    onboarding_mic_granted: "الميكروفون جاهز! انقر على الزر أدناه وقل بعض الكلمات.",
    onboarding_mic_tip: "نصيحة: استخدم سماعات الأذن ومكانًا هادئًا للحصول على أفضل النتائج.",
    onboarding_mic_recording: "جارٍ التسجيل... انقر للإيقاف",
    onboarding_mic_play_back: "رائع! استمع إلى تسجيلك:",
    onboarding_mic_success: "الميكروفون الخاص بك يعمل بشكل مثالي!",
    onboarding_mic_record_again: "التسجيل مرة أخرى",
    onboarding_conversation_title: "محادثتك الأولى",
    onboarding_conversation_start_prompt: "انقر على الزر الأخضر وقل {{greeting}} للبدء!",
    onboarding_conversation_done: "محادثة رائعة!",
    onboarding_conversation_complete_hint: "جارٍ إعداد خطة التعلم الشخصية الخاصة بك...",
    onboarding_recap_title: "لقد أكملت محادثتك الأولى!",
    onboarding_recap_description: "عمل رائع! لقد خطوت خطوتك الأولى نحو إتقان {{language}}.",
    onboarding_recap_accomplished: "ما حققته:",
    onboarding_recap_had_conversation: "أجريت محادثتك الأولى بـ {{language}}",
    onboarding_recap_learned_word: "تعلمت كلمة مفردات جديدة لمهنتك",
    onboarding_recap_personalized: "خطة التعلم الخاصة بك يتم إنشاؤها بناءً على محادثتك",
    onboarding_auto_objective: "تعلم {{language}} للاستخدام المهني بناءً على المحادثة التمهيدية",
    onboarding_save_error: "حدث خطأ ما. تم حفظ تقدمك محلياً.",
    onboarding_achievement_title: "يوم من تعلم {{language}} تم إنجازه!",
    onboarding_achievement_summary: "مسار التعلم الشخصي الخاص بك في {{language}} جاهز. واصل رحلتك في أي وقت!",
    onboarding_achievement_continue: "المتابعة!",
    onboarding_achievement_stop: "التوقف لهذا اليوم",
  },

  hi: {
    // Native Language Step
    onboarding_native_language_title: "आपकी मातृभाषा क्या है?",
    onboarding_native_language_description: "अपने अनुभव को व्यक्तिगत बनाने के लिए अपनी मातृभाषा चुनें।",
    onboarding_more_languages: "और भाषाएं",
    onboarding_show_less_languages: "कम दिखाएं",

    // Target Language Step
    onboarding_target_language_title: "आप कौन सी भाषा सीखना चाहते हैं?",
    onboarding_target_language_description: "वह भाषा चुनें जो आप सीखना चाहते हैं। आप एक स्वागत अभिवादन सुनेंगे।",

    // Age Step
    onboarding_age_title: "क्या आप वयस्क हैं?",
    onboarding_age_yes: "हाँ",
    onboarding_age_no: "नहीं",
    onboarding_age_adult_description: "18 वर्ष या उससे अधिक",
    onboarding_age_child_description: "18 वर्ष से कम",
    onboarding_age_description: "यह हमें आपकी उम्र के अनुसार उचित सामग्री प्रदान करने में मदद करता है।",

    // Level Step (CEFR)
    onboarding_level_title: "आपका वर्तमान स्तर क्या है?",
    onboarding_level_description_cefr: "CEFR स्केल का उपयोग करके अपना दक्षता स्तर चुनें।",
    onboarding_level_unknown: "मुझे नहीं पता",
    onboarding_level_placement_hint: "हम आपकी पहली बातचीत में आपका स्तर निर्धारित करेंगे।",
    cefr_a1_description: "पूर्ण शुरुआती",
    cefr_a2_description: "बुनियादी वाक्यांश और सरल बातचीत",
    cefr_b1_description: "परिचित विषयों पर चर्चा कर सकते हैं",
    cefr_b2_description: "मूल वक्ताओं के साथ बातचीत कर सकते हैं",
    cefr_c1_description: "सामाजिक और पेशेवर उपयोग के लिए धाराप्रवाह",
    cefr_c2_description: "लगभग मूल स्तर",

    // Pronoun Step
    onboarding_pronoun_title: "आपके सर्वनाम क्या हैं?",
    onboarding_pronoun_description: "कुछ भाषाओं में व्याकरणिक लिंग होता है। यह हमें आपसे सही तरीके से बात करने में मदद करता है।",
    pronoun_masculine: "वह (पुरुष)",
    pronoun_feminine: "वह (महिला)",
    pronoun_nonbinary: "वे",
    pronoun_unspecified: "बताना नहीं चाहते",

    // Terms and Conditions
    terms_checkbox_label: "मैं सहमत हूं",
    terms_and_conditions: "नियम और शर्तें",

    // Completion Step
    onboarding_completion_title: "आप तैयार हैं!",
    onboarding_completion_description: "यहाँ आपकी प्राथमिकताओं का सारांश है। आप इन्हें बाद में सेटिंग्स में बदल सकते हैं।",
    start_learning: "सीखना शुरू करें",
    saving: "सहेजा जा रहा है...",
    redirecting: "पुनर्निर्देशित हो रहा है...",
    onboarding_privacy_note: "आपकी प्राथमिकताएं सुरक्षित रूप से संग्रहीत हैं और हमें आपके सीखने के अनुभव को व्यक्तिगत बनाने में मदद करती हैं।",

    // Learning Objective Step
    onboarding_objective_title: "आखिरी बात: आपका सीखने का लक्ष्य क्या है?",
    onboarding_objective_description: "अपने सीखने के मार्ग को व्यक्तिगत बनाने में हमारी मदद के लिए अपना लक्ष्य साझा करें। एक समय सीमा और एक ठोस उदाहरण शामिल करें, जैसे: \"मैं 3 महीने में अंग्रेजी में मीटिंग शुरू करने में सक्षम होना चाहता हूं।\"",
    onboarding_objective_placeholder: "उदा. \"मैं 6 महीने में फ्रेंच में ग्राहकों को प्रेजेंटेशन देने में सक्षम होना चाहता हूं...\"",
    onboarding_objective_or: "या",
    onboarding_objective_record: "रिकॉर्ड करें",
    onboarding_objective_stop: "रुकें",
    onboarding_objective_start_recording: "वॉयस रिकॉर्डिंग शुरू करें",
    onboarding_objective_stop_recording: "रिकॉर्डिंग बंद करें",
    onboarding_objective_transcribing: "ट्रांसक्राइब हो रहा है...",
    onboarding_objective_mic_denied: "माइक्रोफ़ोन एक्सेस अस्वीकृत। कृपया माइक्रोफ़ोन एक्सेस की अनुमति दें और पुनः प्रयास करें।",
    onboarding_objective_transcription_error: "ऑडियो ट्रांसक्राइब करने में विफल। कृपया पुनः प्रयास करें या अपना लक्ष्य टाइप करें।",
    onboarding_objective_skip_hint: "यह चरण वैकल्पिक है। अगर आप अभी तक सुनिश्चित नहीं हैं तो इसे छोड़ सकते हैं।",
    onboarding_objective_entered: "आपने एक सीखने का लक्ष्य दर्ज किया है।",
    onboarding_objective_aria_label: "आपका सीखने का लक्ष्य",

    // Notifications Step
    onboarding_notifications_title: "अभ्यास रिमाइंडर के साथ ट्रैक पर रहें",
    onboarding_finish: "हो गया",
    onboarding_notifications_description: "नियमित अभ्यास भाषा सीखने की कुंजी है। हम आपको नियमित रहने में मदद करने के लिए दोस्ताना रिमाइंडर भेजेंगे।",
    onboarding_notifications_recommendation: "हम हर दिन 10-15 मिनट के छोटे सत्र की सिफारिश करते हैं। स्टैनफोर्ड और कैम्ब्रिज के शोध से पता चलता है कि छोटी, अंतराल वाली प्रैक्टिस लंबे, कम बार वाले सत्रों से अधिक प्रभावी है।",
    onboarding_notifications_audio: "क्या आप चाहते हैं कि मैं आपको अभ्यास रिमाइंडर भेजूं?",
    onboarding_notifications_yes: "हाँ, मुझे याद दिलाएं",
    onboarding_notifications_no: "नहीं धन्यवाद",
    onboarding_notifications_days_label: "कौन से दिन?",
    onboarding_notifications_time_label: "किस समय?",
    onboarding_notifications_email_notice: "हम आपके ईमेल पते पर रिमाइंडर भेजेंगे।",
    day_mon: "सोम",
    day_tue: "मंगल",
    day_wed: "बुध",
    day_thu: "गुरु",
    day_fri: "शुक्र",
    day_sat: "शनि",
    day_sun: "रवि",
    notifications_summary_enabled: "{{count}} दिनों के लिए {{time}} पर रिमाइंडर सक्षम",
    notifications_summary_disabled: "रिमाइंडर अक्षम हैं",

    // Calendar section
    onboarding_calendar_description: "या अपने कैलेंडर में जोड़ें",
    onboarding_calendar_google: "Google कैलेंडर",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: ".ics डाउनलोड करें",
    onboarding_calendar_outlook_hint: "सुझाव: जोड़ने के बाद Outlook में दोहराव के रूप में सेट करें",
    onboarding_calendar_added: "जोड़ा गया",

    // Email checkbox
    onboarding_email_checkbox_label: "मुझे ईमेल रिमाइंडर भेजें",

    // Warning
    onboarding_no_reminders_warning: "कोई रिमाइंडर सेट नहीं है। नियमित अभ्यास आपको तेजी से सीखने में मदद करता है!",

    // Onboarding step titles
    onboarding_step_native_language: "मातृभाषा",
    onboarding_step_target_language: "सीखने की लक्ष्य भाषा",
    onboarding_step_age: "उम्र",
    onboarding_step_level: "स्तर",
    onboarding_step_pronoun: "सर्वनाम",
    onboarding_step_objective: "लक्ष्य",
    onboarding_step_notifications: "रिमाइंडर",
    onboarding_step_completion: "पूर्ण करें",

    // V2 Steps
    onboarding_welcome_title: "स्वागत है!",
    onboarding_welcome_text: "कुछ सवालों और आपकी पहली काम से जुड़ी बातचीत से शुरू करते हैं।",
    onboarding_welcome_duration: "सेटअप में लगभग 2 मिनट लगते हैं, फिर एक छोटी पहली बातचीत।",
    onboarding_step_ready: "तैयार",
    onboarding_ready_title: "आप तैयार हैं!",
    onboarding_ready_description: "अगला, आप एक छोटी पहली बातचीत करेंगे ताकि मैं आपको जान सकूं और आपको आपका पहला शब्द सिखा सकूं।",
    onboarding_ready_learning: "सीख रहे हैं",
    onboarding_ready_level: "स्तर",
    onboarding_ready_start: "मेरी पहली बातचीत शुरू करें",
    onboarding_ready_not_now: "अभी नहीं",
    onboarding_mic_listening: "सुन रहा हूँ… कुछ शब्द कहें",
    onboarding_mic_hear_yes: "मुझे सुनाई दे रहा है",
    onboarding_mic_hear_no: "मुझे सुनाई नहीं दे रहा",
    onboarding_mic_troubleshoot: "कोई बात नहीं — जांचें कि सही माइक्रोफ़ोन चुना गया है और वॉल्यूम चालू है, फिर दोबारा रिकॉर्ड करें।",
    onboarding_mic_playback_confirm: "अपनी रिकॉर्डिंग सुनें — क्या आप खुद को सुन सकते हैं?",
    onboarding_accept_continue: "स्वीकार करें और जारी रखें",
    onboarding_back: "वापस",
    onboarding_how_it_works_title: "यह कैसे काम करता है",
    onboarding_how_it_works_text: "मैं आपकी मातृभाषा और लक्ष्य भाषा दोनों समझता हूँ। मैं दोनों का उपयोग करूंगा ताकि आपको सीखने में मदद मिले, लेकिन अपनी नई भाषा का जितना हो सके उपयोग करें!",
    onboarding_level_beginner_description: "शुरुआती के लिए बोलने का कौशल",
    level_beginner: "शुरुआती",
    level_intermediate: "मध्यवर्ती",
    level_advanced: "उन्नत",
    level_beginner_description: "मैं बुनियादी वाक्यांशों और अभिव्यक्तियों को समझ और उपयोग कर सकता/सकती हूँ",
    level_intermediate_description: "मैं परिचित विषयों पर बातचीत कर सकता/सकती हूँ और अनुभव बता सकता/सकती हूँ",
    level_advanced_description: "मैं अधिकांश स्थितियों में धाराप्रवाह और सहज रूप से अपनी बात रख सकता/सकती हूँ",
    onboarding_level_title_v2: "आप {{language}} में क्या करने में सक्षम हैं?",
    onboarding_level_question: "आप {{language}} में क्या करने में सक्षम हैं?",
    language_fr: "फ़्रेंच",
    language_en: "अंग्रेज़ी",
    language_es: "स्पेनिश",
    onboarding_level_intermediate_description: "मध्यवर्ती के लिए बोलने का कौशल",
    onboarding_level_advanced_description: "उन्नत के लिए बोलने का कौशल",
    onboarding_adaptation_title: "यहाँ बताया गया है कि मैं आपके लिए कैसे अनुकूल होऊंगा",
    onboarding_adaptation_beginner: "चूंकि आपने **{{level}}** चुना है, मैं मुख्यतः {{nativeLanguage}} में बोलूंगा और धीरे-धीरे {{targetLanguage}} के शब्द और वाक्यांश शामिल करूंगा। मैं आपके शब्द-भंडार और बुनियादी वाक्यों पर ध्यान दूंगा।",
    onboarding_adaptation_intermediate: "चूंकि आपने **{{level}}** चुना है, मैं मुख्यतः {{targetLanguage}} में बोलूंगा, जरूरत पड़ने पर {{nativeLanguage}} में समझाऊंगा। मैं आपकी व्याकरण और बातचीत के कौशल को बेहतर बनाने में मदद करूंगा।",
    onboarding_adaptation_advanced: "चूंकि आपने **{{level}}** चुना है, मैं लगभग पूरी तरह {{targetLanguage}} में बोलूंगा। मैं आपकी अभिव्यक्ति, मुहावरों और पेशेवर भाषा को निखारने पर ध्यान दूंगा।",
    onboarding_adaptation_advanced_tip: "💡 एक उन्नत शिक्षार्थी के रूप में, आप रीयल-टाइम मोड का आनंद ले सकते हैं — एक तेज़, निर्बाध बातचीत अनुभव। आप इसे बाद में सेटिंग्स में सक्षम कर सकते हैं।",
    onboarding_mic_title: "आपका माइक्रोफोन जांचते हैं",
    onboarding_mic_description: "हम बातचीत के लिए आपके माइक्रोफोन का उपयोग करेंगे। सुनिश्चित करें कि यह काम कर रहा है!",
    onboarding_mic_permission_prompt: "सबसे पहले, हमें आपके माइक्रोफोन तक पहुंच चाहिए।",
    onboarding_mic_allow: "माइक्रोफोन अनुमति दें",
    onboarding_mic_requesting: "माइक्रोफोन एक्सेस का अनुरोध किया जा रहा है...",
    onboarding_mic_denied: "माइक्रोफोन एक्सेस अस्वीकार कर दी गई। कृपया इसे अपने ब्राउज़र सेटिंग में सक्षम करें और पुनः प्रयास करें।",
    onboarding_mic_retry: "पुनः प्रयास करें",
    onboarding_mic_skip: "इस चरण को छोड़ें",
    onboarding_mic_ready: "आपका माइक्रोफोन तैयार है! रिकॉर्ड बटन पर क्लिक करें और कुछ शब्द कहें।",
    onboarding_mic_granted: "माइक्रोफोन तैयार है! नीचे दिए गए बटन पर क्लिक करें और कुछ शब्द कहें।",
    onboarding_mic_tip: "सुझाव: सर्वोत्तम परिणामों के लिए हेडफ़ोन और शांत जगह का उपयोग करें।",
    onboarding_mic_recording: "रिकॉर्डिंग हो रही है... रोकने के लिए क्लिक करें",
    onboarding_mic_play_back: "बढ़िया! अपनी रिकॉर्डिंग सुनें:",
    onboarding_mic_success: "आपका माइक्रोफोन बिल्कुल सही काम कर रहा है!",
    onboarding_mic_record_again: "फिर से रिकॉर्ड करें",
    onboarding_conversation_title: "आपकी पहली बातचीत",
    onboarding_conversation_start_prompt: "हरे बटन पर क्लिक करें और शुरू करने के लिए {{greeting}} कहें!",
    onboarding_conversation_done: "शानदार बातचीत!",
    onboarding_conversation_complete_hint: "आपका व्यक्तिगत सीखने का प्लान तैयार किया जा रहा है...",
    onboarding_recap_title: "आपने अपनी पहली बातचीत पूरी की!",
    onboarding_recap_description: "बहुत अच्छा! आपने {{language}} में प्रवाह की दिशा में पहला कदम उठाया।",
    onboarding_recap_accomplished: "आपने क्या हासिल किया:",
    onboarding_recap_had_conversation: "{{language}} में पहली बातचीत की",
    onboarding_recap_learned_word: "अपने पेशे के लिए एक नया शब्द सीखा",
    onboarding_recap_personalized: "आपकी बातचीत के आधार पर आपका सीखने का प्लान बनाया जा रहा है",
    onboarding_auto_objective: "परिचयात्मक बातचीत के आधार पर पेशेवर उपयोग के लिए {{language}} सीखें",
    onboarding_save_error: "कुछ गलत हो गया। आपकी प्रगति स्थानीय रूप से सहेजी गई।",
    onboarding_achievement_title: "{{language}} सीखने का एक दिन पूरा!",
    onboarding_achievement_summary: "आपका व्यक्तिगत {{language}} सीखने का मार्ग तैयार है। कभी भी अपनी यात्रा जारी रखें!",
    onboarding_achievement_continue: "जारी रखें!",
    onboarding_achievement_stop: "आज के लिए रुकें",
  },

  vi: {
    // Native Language Step
    onboarding_native_language_title: "Ngôn ngữ mẹ đẻ của bạn là gì?",
    onboarding_native_language_description: "Chọn ngôn ngữ mẹ đẻ để cá nhân hóa trải nghiệm của bạn.",
    onboarding_more_languages: "Thêm ngôn ngữ",
    onboarding_show_less_languages: "Thu gọn",

    // Target Language Step
    onboarding_target_language_title: "Bạn muốn học ngôn ngữ nào?",
    onboarding_target_language_description: "Chọn ngôn ngữ bạn muốn học. Bạn sẽ nghe lời chào mẫu.",

    // Age Step
    onboarding_age_title: "Bạn là người lớn?",
    onboarding_age_yes: "Có",
    onboarding_age_no: "Không",
    onboarding_age_adult_description: "18 tuổi trở lên",
    onboarding_age_child_description: "Dưới 18 tuổi",
    onboarding_age_description: "Điều này giúp chúng tôi cung cấp nội dung phù hợp với độ tuổi của bạn.",

    // Level Step (CEFR)
    onboarding_level_title: "Trình độ hiện tại của bạn là gì?",
    onboarding_level_description_cefr: "Chọn trình độ thành thạo của bạn theo thang CEFR.",
    onboarding_level_unknown: "Tôi không biết",
    onboarding_level_placement_hint: "Chúng tôi sẽ xác định trình độ của bạn trong cuộc trò chuyện đầu tiên.",
    cefr_a1_description: "Người mới bắt đầu hoàn toàn",
    cefr_a2_description: "Cụm từ cơ bản và hội thoại đơn giản",
    cefr_b1_description: "Có thể thảo luận các chủ đề quen thuộc",
    cefr_b2_description: "Có thể giao tiếp với người bản xứ",
    cefr_c1_description: "Thành thạo cho giao tiếp xã hội và nghề nghiệp",
    cefr_c2_description: "Trình độ gần như người bản xứ",

    // Pronoun Step
    onboarding_pronoun_title: "Đại từ của bạn là gì?",
    onboarding_pronoun_description: "Một số ngôn ngữ có giới tính ngữ pháp. Điều này giúp chúng tôi nói chuyện với bạn chính xác.",
    pronoun_masculine: "Anh ấy",
    pronoun_feminine: "Cô ấy",
    pronoun_nonbinary: "Họ",
    pronoun_unspecified: "Không muốn nói",

    // Terms and Conditions
    terms_checkbox_label: "Tôi đồng ý với",
    terms_and_conditions: "Điều khoản và Điều kiện",

    // Completion Step
    onboarding_completion_title: "Bạn đã sẵn sàng!",
    onboarding_completion_description: "Đây là tóm tắt các tùy chọn của bạn. Bạn có thể thay đổi chúng sau trong cài đặt.",
    start_learning: "Bắt đầu học",
    saving: "Đang lưu...",
    redirecting: "Đang chuyển hướng...",
    onboarding_privacy_note: "Các tùy chọn của bạn được lưu trữ an toàn và giúp chúng tôi cá nhân hóa trải nghiệm học tập của bạn.",

    // Learning Objective Step
    onboarding_objective_title: "Điều cuối cùng: Mục tiêu học tập của bạn là gì?",
    onboarding_objective_description: "Chia sẻ mục tiêu của bạn để giúp chúng tôi cá nhân hóa lộ trình học tập. Bao gồm khung thời gian và ví dụ cụ thể, như: \"Tôi muốn có thể bắt đầu cuộc họp bằng tiếng Anh trong 3 tháng.\"",
    onboarding_objective_placeholder: "Ví dụ: \"Tôi muốn có thể thuyết trình cho khách hàng bằng tiếng Pháp trong 6 tháng...\"",
    onboarding_objective_or: "hoặc",
    onboarding_objective_record: "Ghi âm",
    onboarding_objective_stop: "Dừng",
    onboarding_objective_start_recording: "Bắt đầu ghi âm giọng nói",
    onboarding_objective_stop_recording: "Dừng ghi âm",
    onboarding_objective_transcribing: "Đang chuyển đổi...",
    onboarding_objective_mic_denied: "Quyền truy cập micrô bị từ chối. Vui lòng cho phép truy cập micrô và thử lại.",
    onboarding_objective_transcription_error: "Không thể chuyển đổi âm thanh. Vui lòng thử lại hoặc nhập mục tiêu của bạn.",
    onboarding_objective_skip_hint: "Bước này là tùy chọn. Bạn có thể bỏ qua nếu chưa chắc chắn.",
    onboarding_objective_entered: "Bạn đã nhập mục tiêu học tập.",
    onboarding_objective_aria_label: "Mục tiêu học tập của bạn",

    // Notifications Step
    onboarding_notifications_title: "Giữ đúng hướng với lời nhắc luyện tập",
    onboarding_finish: "Xong",
    onboarding_notifications_description: "Luyện tập thường xuyên là chìa khóa để học ngôn ngữ. Chúng tôi sẽ gửi cho bạn những lời nhắc thân thiện để giúp bạn duy trì đều đặn.",
    onboarding_notifications_recommendation: "Chúng tôi khuyên bạn nên luyện tập 10-15 phút mỗi ngày. Nghiên cứu từ Stanford và Cambridge cho thấy việc luyện tập ngắn, đều đặn hiệu quả hơn các buổi học dài và ít thường xuyên.",
    onboarding_notifications_audio: "Bạn có muốn tôi gửi lời nhắc luyện tập không?",
    onboarding_notifications_yes: "Có, nhắc tôi",
    onboarding_notifications_no: "Không, cảm ơn",
    onboarding_notifications_days_label: "Những ngày nào?",
    onboarding_notifications_time_label: "Vào lúc mấy giờ?",
    onboarding_notifications_email_notice: "Chúng tôi sẽ gửi lời nhắc đến địa chỉ email của bạn.",
    day_mon: "T2",
    day_tue: "T3",
    day_wed: "T4",
    day_thu: "T5",
    day_fri: "T6",
    day_sat: "T7",
    day_sun: "CN",
    notifications_summary_enabled: "Đã bật lời nhắc cho {{count}} ngày lúc {{time}}",
    notifications_summary_disabled: "Lời nhắc đã tắt",

    // Calendar section
    onboarding_calendar_description: "Hoặc thêm vào lịch của bạn",
    onboarding_calendar_google: "Google Calendar",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: "Tải .ics",
    onboarding_calendar_outlook_hint: "Mẹo: Đặt làm lặp lại trong Outlook sau khi thêm",
    onboarding_calendar_added: "Đã thêm",

    // Email checkbox
    onboarding_email_checkbox_label: "Gửi lời nhắc qua email cho tôi",

    // Warning
    onboarding_no_reminders_warning: "Chưa đặt lời nhắc nào. Luyện tập đều đặn giúp bạn học nhanh hơn!",

    // Onboarding step titles
    onboarding_step_native_language: "Ngôn ngữ mẹ đẻ",
    onboarding_step_target_language: "Ngôn ngữ muốn học",
    onboarding_step_age: "Tuổi",
    onboarding_step_level: "Trình độ",
    onboarding_step_pronoun: "Đại từ",
    onboarding_step_objective: "Mục tiêu",
    onboarding_step_notifications: "Nhắc nhở",
    onboarding_step_completion: "Hoàn thành",

    // V2 Steps
    onboarding_welcome_title: "Chào mừng!",
    onboarding_welcome_text: "Hãy bắt đầu với vài câu hỏi và cuộc trò chuyện đầu tiên liên quan đến công việc của bạn.",
    onboarding_welcome_duration: "Thiết lập mất khoảng 2 phút, sau đó là một cuộc trò chuyện đầu tiên ngắn.",
    onboarding_step_ready: "Sẵn sàng",
    onboarding_ready_title: "Bạn đã sẵn sàng!",
    onboarding_ready_description: "Tiếp theo, bạn sẽ có một cuộc trò chuyện đầu tiên ngắn để tôi có thể làm quen với bạn và dạy bạn từ đầu tiên.",
    onboarding_ready_learning: "Đang học",
    onboarding_ready_level: "Trình độ",
    onboarding_ready_start: "Bắt đầu cuộc trò chuyện đầu tiên của tôi",
    onboarding_ready_not_now: "Chưa phải bây giờ",
    onboarding_mic_listening: "Đang lắng nghe… nói vài từ",
    onboarding_mic_hear_yes: "Tôi nghe được",
    onboarding_mic_hear_no: "Tôi không nghe được",
    onboarding_mic_troubleshoot: "Đừng lo — hãy kiểm tra xem đã chọn đúng micrô chưa và âm lượng đã bật chưa, rồi ghi âm lại.",
    onboarding_mic_playback_confirm: "Nghe lại bản ghi âm của bạn — bạn có nghe thấy chính mình không?",
    onboarding_accept_continue: "Chấp nhận & Tiếp tục",
    onboarding_back: "Quay lại",
    onboarding_how_it_works_title: "Cách hoạt động",
    onboarding_how_it_works_text: "Tôi hiểu cả tiếng mẹ đẻ và ngôn ngữ bạn đang học. Tôi sẽ dùng cả hai để giúp bạn, nhưng hãy cố gắng sử dụng ngôn ngữ mới càng nhiều càng tốt!",
    onboarding_level_beginner_description: "Kỹ năng nói cho người mới bắt đầu",
    level_beginner: "Người mới",
    level_intermediate: "Trung cấp",
    level_advanced: "Nâng cao",
    level_beginner_description: "Tôi có thể hiểu và sử dụng các cụm từ và biểu đạt cơ bản",
    level_intermediate_description: "Tôi có thể trò chuyện về các chủ đề quen thuộc và mô tả trải nghiệm",
    level_advanced_description: "Tôi có thể diễn đạt một cách lưu loát và tự nhiên trong hầu hết các tình huống",
    onboarding_level_title_v2: "Bạn có thể làm gì bằng {{language}}?",
    onboarding_level_question: "Bạn có thể làm gì bằng {{language}}?",
    language_fr: "tiếng Pháp",
    language_en: "tiếng Anh",
    language_es: "tiếng Tây Ban Nha",
    onboarding_level_intermediate_description: "Kỹ năng nói cho trình độ trung cấp",
    onboarding_level_advanced_description: "Kỹ năng nói cho trình độ nâng cao",
    onboarding_adaptation_title: "Đây là cách tôi sẽ thích nghi cho bạn",
    onboarding_adaptation_beginner: "Vì bạn đã chọn **{{level}}**, tôi sẽ nói chủ yếu bằng {{nativeLanguage}} và dần dần giới thiệu các từ và cụm từ bằng {{targetLanguage}}. Tôi sẽ tập trung vào việc xây dựng vốn từ vựng và các câu cơ bản của bạn.",
    onboarding_adaptation_intermediate: "Vì bạn đã chọn **{{level}}**, tôi sẽ nói chủ yếu bằng {{targetLanguage}} với giải thích bằng {{nativeLanguage}} khi cần. Tôi sẽ giúp bạn cải thiện ngữ pháp và kỹ năng hội thoại.",
    onboarding_adaptation_advanced: "Vì bạn đã chọn **{{level}}**, tôi sẽ nói gần như hoàn toàn bằng {{targetLanguage}}. Tôi sẽ tập trung vào việc tinh chỉnh cách diễn đạt, thành ngữ và ngôn ngữ chuyên nghiệp của bạn.",
    onboarding_adaptation_advanced_tip: "💡 Là người học nâng cao, bạn có thể thích chế độ Thời gian thực — trải nghiệm trò chuyện nhanh hơn, không bị gián đoạn. Bạn có thể bật tính năng này sau trong Cài đặt.",
    onboarding_mic_title: "Hãy kiểm tra micrô của bạn",
    onboarding_mic_description: "Chúng tôi sẽ sử dụng micrô của bạn cho các cuộc trò chuyện. Hãy đảm bảo nó hoạt động!",
    onboarding_mic_permission_prompt: "Trước tiên, chúng tôi cần quyền truy cập vào micrô của bạn.",
    onboarding_mic_allow: "Cho phép Micrô",
    onboarding_mic_requesting: "Đang yêu cầu quyền truy cập micrô...",
    onboarding_mic_denied: "Quyền truy cập micrô đã bị từ chối. Vui lòng bật nó trong cài đặt trình duyệt và thử lại.",
    onboarding_mic_retry: "Thử lại",
    onboarding_mic_skip: "Bỏ qua bước này",
    onboarding_mic_ready: "Micrô của bạn đã sẵn sàng! Nhấp vào nút ghi âm và nói vài từ.",
    onboarding_mic_granted: "Micrô đã sẵn sàng! Nhấp vào nút bên dưới và nói vài từ.",
    onboarding_mic_tip: "Mẹo: Sử dụng tai nghe và nơi yên tĩnh để có kết quả tốt nhất.",
    onboarding_mic_recording: "Đang ghi âm... Nhấp để dừng",
    onboarding_mic_play_back: "Tuyệt vời! Nghe lại bản ghi âm của bạn:",
    onboarding_mic_success: "Micrô của bạn hoạt động hoàn hảo!",
    onboarding_mic_record_again: "Ghi âm lại",
    onboarding_conversation_title: "Cuộc trò chuyện đầu tiên của bạn",
    onboarding_conversation_start_prompt: "Nhấp vào nút màu xanh lá và nói {{greeting}} để bắt đầu!",
    onboarding_conversation_done: "Cuộc trò chuyện tuyệt vời!",
    onboarding_conversation_complete_hint: "Đang thiết lập kế hoạch học tập cá nhân của bạn...",
    onboarding_recap_title: "Bạn đã hoàn thành cuộc trò chuyện đầu tiên!",
    onboarding_recap_description: "Làm tốt lắm! Bạn đã thực hiện bước đầu tiên hướng tới sự thành thạo {{language}}.",
    onboarding_recap_accomplished: "Những gì bạn đã đạt được:",
    onboarding_recap_had_conversation: "Đã có cuộc trò chuyện đầu tiên bằng {{language}}",
    onboarding_recap_learned_word: "Đã học một từ vựng mới cho nghề nghiệp của bạn",
    onboarding_recap_personalized: "Kế hoạch học tập của bạn đang được tạo dựa trên cuộc trò chuyện của bạn",
    onboarding_auto_objective: "Học {{language}} để sử dụng chuyên nghiệp dựa trên cuộc trò chuyện giới thiệu",
    onboarding_save_error: "Đã xảy ra sự cố. Tiến trình của bạn đã được lưu cục bộ.",
    onboarding_achievement_title: "ngày học {{language}} đã hoàn thành!",
    onboarding_achievement_summary: "Lộ trình học {{language}} cá nhân của bạn đã sẵn sàng. Tiếp tục hành trình của bạn bất cứ lúc nào!",
    onboarding_achievement_continue: "Tiếp tục!",
    onboarding_achievement_stop: "Dừng lại cho hôm nay",
  },

  pl: {
    // Native Language Step
    onboarding_native_language_title: "Jaki jest Twój język ojczysty?",
    onboarding_native_language_description: "Wybierz swój język ojczysty, aby spersonalizować swoje doświadczenie.",
    onboarding_more_languages: "Więcej języków",
    onboarding_show_less_languages: "Pokaż mniej",

    // Target Language Step
    onboarding_target_language_title: "Jakiego języka chcesz się nauczyć?",
    onboarding_target_language_description: "Wybierz język, którego chcesz się uczyć. Usłyszysz przykładowe powitanie.",

    // Age Step
    onboarding_age_title: "Czy jesteś osobą dorosłą?",
    onboarding_age_yes: "Tak",
    onboarding_age_no: "Nie",
    onboarding_age_adult_description: "18 lat lub więcej",
    onboarding_age_child_description: "Poniżej 18 lat",
    onboarding_age_description: "Pomaga nam to dostarczać treści odpowiednie do Twojego wieku.",

    // Level Step (CEFR)
    onboarding_level_title: "Jaki jest Twój obecny poziom?",
    onboarding_level_description_cefr: "Wybierz swój poziom biegłości według skali CEFR.",
    onboarding_level_unknown: "Nie wiem",
    onboarding_level_placement_hint: "Określimy Twój poziom podczas pierwszej rozmowy.",
    cefr_a1_description: "Całkowity początkujący",
    cefr_a2_description: "Podstawowe zwroty i proste rozmowy",
    cefr_b1_description: "Może dyskutować o znanych tematach",
    cefr_b2_description: "Może rozmawiać z native speakerami",
    cefr_c1_description: "Płynność w użyciu społecznym i zawodowym",
    cefr_c2_description: "Poziom niemal natywny",

    // Pronoun Step
    onboarding_pronoun_title: "Jakie są Twoje zaimki?",
    onboarding_pronoun_description: "Niektóre języki mają rodzaj gramatyczny. Pomaga nam to zwracać się do Ciebie poprawnie.",
    pronoun_masculine: "On",
    pronoun_feminine: "Ona",
    pronoun_nonbinary: "Oni/One",
    pronoun_unspecified: "Wolę nie mówić",

    // Terms and Conditions
    terms_checkbox_label: "Akceptuję",
    terms_and_conditions: "Regulamin",

    // Completion Step
    onboarding_completion_title: "Wszystko gotowe!",
    onboarding_completion_description: "Oto podsumowanie Twoich preferencji. Możesz je zmienić później w ustawieniach.",
    start_learning: "Zacznij się uczyć",
    saving: "Zapisywanie...",
    redirecting: "Przekierowywanie...",
    onboarding_privacy_note: "Twoje preferencje są bezpiecznie przechowywane i pomagają nam personalizować Twoje doświadczenie nauki.",

    // Learning Objective Step
    onboarding_objective_title: "Ostatnia rzecz: Jaki jest Twój cel nauki?",
    onboarding_objective_description: "Podziel się swoim celem, aby pomóc nam spersonalizować Twoją ścieżkę nauki. Podaj ramy czasowe i konkretny przykład, np.: \"Chcę móc rozpocząć spotkanie po angielsku za 3 miesiące.\"",
    onboarding_objective_placeholder: "np. \"Chcę móc prezentować klientom po francusku w ciągu 6 miesięcy...\"",
    onboarding_objective_or: "lub",
    onboarding_objective_record: "Nagrywaj",
    onboarding_objective_stop: "Stop",
    onboarding_objective_start_recording: "Rozpocznij nagrywanie głosu",
    onboarding_objective_stop_recording: "Zatrzymaj nagrywanie",
    onboarding_objective_transcribing: "Transkrybuję...",
    onboarding_objective_mic_denied: "Odmowa dostępu do mikrofonu. Proszę zezwolić na dostęp do mikrofonu i spróbować ponownie.",
    onboarding_objective_transcription_error: "Nie udało się transkrybować audio. Proszę spróbować ponownie lub wpisać swój cel.",
    onboarding_objective_skip_hint: "Ten krok jest opcjonalny. Możesz go pominąć, jeśli nie jesteś jeszcze pewien.",
    onboarding_objective_entered: "Wprowadzono cel nauki.",
    onboarding_objective_aria_label: "Twój cel nauki",

    // Notifications Step
    onboarding_notifications_title: "Bądź na bieżąco z przypomnieniami o ćwiczeniach",
    onboarding_finish: "Gotowe",
    onboarding_notifications_description: "Regularne ćwiczenie jest kluczem do nauki języków. Będziemy wysyłać przyjazne przypomnienia, aby pomóc Ci zachować systematyczność.",
    onboarding_notifications_recommendation: "Zalecamy krótkie sesje po 10-15 minut każdego dnia. Badania ze Stanford i Cambridge pokazują, że krótka, rozłożona praktyka jest skuteczniejsza niż długie, rzadkie sesje.",
    onboarding_notifications_audio: "Czy chcesz, żebym wysyłał Ci przypomnienia o ćwiczeniach?",
    onboarding_notifications_yes: "Tak, przypomnij mi",
    onboarding_notifications_no: "Nie, dziękuję",
    onboarding_notifications_days_label: "Które dni?",
    onboarding_notifications_time_label: "O której godzinie?",
    onboarding_notifications_email_notice: "Będziemy wysyłać przypomnienia na Twój adres e-mail.",
    day_mon: "Pon",
    day_tue: "Wt",
    day_wed: "Śr",
    day_thu: "Czw",
    day_fri: "Pt",
    day_sat: "Sob",
    day_sun: "Nd",
    notifications_summary_enabled: "Przypomnienia włączone na {{count}} dni o {{time}}",
    notifications_summary_disabled: "Przypomnienia są wyłączone",

    // Calendar section
    onboarding_calendar_description: "Lub dodaj do swojego kalendarza",
    onboarding_calendar_google: "Kalendarz Google",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: "Pobierz .ics",
    onboarding_calendar_outlook_hint: "Wskazówka: Ustaw jako cykliczne w Outlook po dodaniu",
    onboarding_calendar_added: "Dodano",

    // Email checkbox
    onboarding_email_checkbox_label: "Wysyłaj mi przypomnienia e-mail",

    // Warning
    onboarding_no_reminders_warning: "Nie ustawiono przypomnień. Regularne ćwiczenie pomaga szybciej się uczyć!",

    // Onboarding step titles
    onboarding_step_native_language: "Język ojczysty",
    onboarding_step_target_language: "Język do nauki",
    onboarding_step_age: "Wiek",
    onboarding_step_level: "Poziom",
    onboarding_step_pronoun: "Zaimki",
    onboarding_step_objective: "Cel",
    onboarding_step_notifications: "Przypomnienia",
    onboarding_step_completion: "Zakończenie",

    // V2 Steps
    onboarding_welcome_title: "Witaj!",
    onboarding_welcome_text: "Zacznijmy od kilku pytań i Twojej pierwszej rozmowy związanej z pracą.",
    onboarding_welcome_duration: "Konfiguracja zajmuje około 2 minut, a potem krótka pierwsza rozmowa.",
    onboarding_step_ready: "Gotowe",
    onboarding_ready_title: "Wszystko gotowe!",
    onboarding_ready_description: "Następnie odbędziesz krótką pierwszą rozmowę, abym mógł Cię poznać i nauczyć Cię pierwszego słowa.",
    onboarding_ready_learning: "Nauka",
    onboarding_ready_level: "Poziom",
    onboarding_ready_start: "Rozpocznij moją pierwszą rozmowę",
    onboarding_ready_not_now: "Nie teraz",
    onboarding_mic_listening: "Słucham… powiedz kilka słów",
    onboarding_mic_hear_yes: "Słyszę się",
    onboarding_mic_hear_no: "Nie słyszę się",
    onboarding_mic_troubleshoot: "Bez obaw — sprawdź, czy wybrany jest właściwy mikrofon i czy głośność jest podkręcona, a następnie nagraj ponownie.",
    onboarding_mic_playback_confirm: "Posłuchaj swojego nagrania — czy słyszysz siebie?",
    onboarding_accept_continue: "Akceptuj i kontynuuj",
    onboarding_back: "Wstecz",
    onboarding_how_it_works_title: "Jak to działa",
    onboarding_how_it_works_text: "Rozumiem zarówno Twój język ojczysty, jak i język docelowy. Będę używać obu, aby pomóc Ci się uczyć, ale staraj się używać nowego języka jak najczęściej!",
    onboarding_level_beginner_description: "Umiejętności mówienia dla początkujących",
    level_beginner: "Początkujący",
    level_intermediate: "Średniozaawansowany",
    level_advanced: "Zaawansowany",
    level_beginner_description: "Potrafię zrozumieć i używać podstawowych zwrotów i wyrażeń",
    level_intermediate_description: "Potrafię prowadzić rozmowy na znane tematy i opisywać doświadczenia",
    level_advanced_description: "Potrafię wypowiadać się płynnie i spontanicznie w większości sytuacji",
    onboarding_level_title_v2: "Co potrafisz robić w {{language}}?",
    onboarding_level_question: "Co potrafisz robić w {{language}}?",
    language_fr: "francuski",
    language_en: "angielski",
    language_es: "hiszpański",
    onboarding_level_intermediate_description: "Umiejętności mówienia dla średniozaawansowanych",
    onboarding_level_advanced_description: "Umiejętności mówienia dla zaawansowanych",
    onboarding_adaptation_title: "Oto jak się dostosuje do Ciebie",
    onboarding_adaptation_beginner: "Ponieważ wybrałeś **{{level}}**, będę mówić głównie po {{nativeLanguage}} i stopniowo wprowadzać słowa i zwroty w {{targetLanguage}}. Skupię się na budowaniu Twojego słownictwa i podstawowych zdań.",
    onboarding_adaptation_intermediate: "Ponieważ wybrałeś **{{level}}**, będę mówić głównie po {{targetLanguage}} z wyjaśnieniami po {{nativeLanguage}} w razie potrzeby. Pomogę Ci poprawić gramatykę i umiejętności konwersacyjne.",
    onboarding_adaptation_advanced: "Ponieważ wybrałeś **{{level}}**, będę mówić prawie wyłącznie po {{targetLanguage}}. Skupię się na doskonaleniu Twojej ekspresji, idiomów i języka profesjonalnego.",
    onboarding_adaptation_advanced_tip: "💡 Jako zaawansowany uczeń możesz polubić tryb Czasu rzeczywistego — szybsze, nieprzerwane doświadczenie rozmowy. Możesz go włączyć później w Ustawieniach.",
    onboarding_mic_title: "Przetestujmy Twój mikrofon",
    onboarding_mic_description: "Będziemy używać Twojego mikrofonu do rozmów. Upewnijmy się, że działa!",
    onboarding_mic_permission_prompt: "Najpierw potrzebujemy dostępu do Twojego mikrofonu.",
    onboarding_mic_allow: "Zezwól na mikrofon",
    onboarding_mic_requesting: "Prośba o dostęp do mikrofonu...",
    onboarding_mic_denied: "Dostęp do mikrofonu został odrzucony. Włącz go w ustawieniach przeglądarki i spróbuj ponownie.",
    onboarding_mic_retry: "Spróbuj ponownie",
    onboarding_mic_skip: "Pomiń ten krok",
    onboarding_mic_ready: "Twój mikrofon jest gotowy! Kliknij przycisk nagrywania i powiedz kilka słów.",
    onboarding_mic_granted: "Mikrofon gotowy! Kliknij przycisk poniżej i powiedz kilka słów.",
    onboarding_mic_tip: "Wskazówka: Użyj słuchawek i cichego miejsca, aby uzyskać najlepsze wyniki.",
    onboarding_mic_recording: "Nagrywanie... Kliknij, aby zatrzymać",
    onboarding_mic_play_back: "Świetnie! Posłuchaj swojego nagrania:",
    onboarding_mic_success: "Twój mikrofon działa doskonale!",
    onboarding_mic_record_again: "Nagraj ponownie",
    onboarding_conversation_title: "Twoja pierwsza rozmowa",
    onboarding_conversation_start_prompt: "Kliknij zielony przycisk i powiedz {{greeting}}, aby zacząć!",
    onboarding_conversation_done: "Świetna rozmowa!",
    onboarding_conversation_complete_hint: "Tworzenie spersonalizowanego planu nauki...",
    onboarding_recap_title: "Ukończyłeś swoją pierwszą rozmowę!",
    onboarding_recap_description: "Świetna robota! Zrobiłeś pierwszy krok w kierunku płynności w {{language}}.",
    onboarding_recap_accomplished: "Co osiągnąłeś:",
    onboarding_recap_had_conversation: "Miałeś swoją pierwszą rozmowę w {{language}}",
    onboarding_recap_learned_word: "Nauczyłeś się nowego słowa słownikowego dla swojego zawodu",
    onboarding_recap_personalized: "Twój plan nauki jest tworzony na podstawie Twojej rozmowy",
    onboarding_auto_objective: "Nauka {{language}} do użytku zawodowego na podstawie rozmowy wprowadzającej",
    onboarding_save_error: "Coś poszło nie tak. Twój postęp został zapisany lokalnie.",
    onboarding_achievement_title: "dzień nauki {{language}} zaliczony!",
    onboarding_achievement_summary: "Twoja spersonalizowana ścieżka nauki {{language}} jest gotowa. Kontynuuj swoją podróż w dowolnym momencie!",
    onboarding_achievement_continue: "Kontynuuj!",
    onboarding_achievement_stop: "Zakończ na dzisiaj",
  },

  tr: {
    // Native Language Step
    onboarding_native_language_title: "Ana diliniz nedir?",
    onboarding_native_language_description: "Deneyiminizi kişiselleştirmek için ana dilinizi seçin.",
    onboarding_more_languages: "Daha fazla dil",
    onboarding_show_less_languages: "Daha az göster",

    // Target Language Step
    onboarding_target_language_title: "Hangi dili öğrenmek istiyorsunuz?",
    onboarding_target_language_description: "Öğrenmek istediğiniz dili seçin. Örnek bir karşılama duyacaksınız.",

    // Age Step
    onboarding_age_title: "Yetişkin misiniz?",
    onboarding_age_yes: "Evet",
    onboarding_age_no: "Hayır",
    onboarding_age_adult_description: "18 yaş ve üzeri",
    onboarding_age_child_description: "18 yaşından küçük",
    onboarding_age_description: "Bu, yaşınıza uygun içerik sunmamıza yardımcı olur.",

    // Level Step (CEFR)
    onboarding_level_title: "Mevcut seviyeniz nedir?",
    onboarding_level_description_cefr: "CEFR ölçeğini kullanarak yeterlilik seviyenizi seçin.",
    onboarding_level_unknown: "Bilmiyorum",
    onboarding_level_placement_hint: "İlk konuşmanızda seviyenizi belirleyeceğiz.",
    cefr_a1_description: "Tam başlangıç",
    cefr_a2_description: "Temel ifadeler ve basit konuşmalar",
    cefr_b1_description: "Tanıdık konuları tartışabilir",
    cefr_b2_description: "Anadil konuşanlarla etkileşim kurabilir",
    cefr_c1_description: "Sosyal ve profesyonel kullanım için akıcı",
    cefr_c2_description: "Anadil seviyesine yakın",

    // Pronoun Step
    onboarding_pronoun_title: "Zamirleriniz nelerdir?",
    onboarding_pronoun_description: "Bazı dillerde dilbilgisel cinsiyet vardır. Bu, size doğru şekilde hitap etmemize yardımcı olur.",
    pronoun_masculine: "O (erkek)",
    pronoun_feminine: "O (kadın)",
    pronoun_nonbinary: "Onlar",
    pronoun_unspecified: "Söylemeyi tercih etmiyorum",

    // Terms and Conditions
    terms_checkbox_label: "Kabul ediyorum",
    terms_and_conditions: "Şartlar ve Koşullar",

    // Completion Step
    onboarding_completion_title: "Hazırsınız!",
    onboarding_completion_description: "İşte tercihlerinizin özeti. Bunları daha sonra ayarlardan değiştirebilirsiniz.",
    start_learning: "Öğrenmeye başla",
    saving: "Kaydediliyor...",
    redirecting: "Yönlendiriliyor...",
    onboarding_privacy_note: "Tercihleriniz güvenli bir şekilde saklanır ve öğrenme deneyiminizi kişiselleştirmemize yardımcı olur.",

    // Learning Objective Step
    onboarding_objective_title: "Son bir şey: Öğrenme hedefiniz nedir?",
    onboarding_objective_description: "Öğrenme yolculuğunuzu kişiselleştirmemize yardımcı olmak için hedefinizi paylaşın. Bir zaman çerçevesi ve somut bir örnek ekleyin, örneğin: \"3 ay içinde İngilizce bir toplantı başlatabilmek istiyorum.\"",
    onboarding_objective_placeholder: "örn. \"6 ay içinde Fransızca müşterilere sunum yapabilmek istiyorum...\"",
    onboarding_objective_or: "veya",
    onboarding_objective_record: "Kayıt",
    onboarding_objective_stop: "Durdur",
    onboarding_objective_start_recording: "Ses kaydını başlat",
    onboarding_objective_stop_recording: "Kaydı durdur",
    onboarding_objective_transcribing: "Dönüştürülüyor...",
    onboarding_objective_mic_denied: "Mikrofon erişimi reddedildi. Lütfen mikrofon erişimine izin verin ve tekrar deneyin.",
    onboarding_objective_transcription_error: "Ses dönüştürülemedi. Lütfen tekrar deneyin veya hedefinizi yazın.",
    onboarding_objective_skip_hint: "Bu adım isteğe bağlıdır. Henüz emin değilseniz atlayabilirsiniz.",
    onboarding_objective_entered: "Bir öğrenme hedefi girdiniz.",
    onboarding_objective_aria_label: "Öğrenme hedefiniz",

    // Notifications Step
    onboarding_notifications_title: "Pratik hatırlatıcılarla yolda kalın",
    onboarding_finish: "Tamam",
    onboarding_notifications_description: "Düzenli pratik, dil öğrenmenin anahtarıdır. Tutarlı kalmanıza yardımcı olmak için size dostça hatırlatmalar göndereceğiz.",
    onboarding_notifications_recommendation: "Her gün 10-15 dakikalık kısa seanslar öneriyoruz. Stanford ve Cambridge'den araştırmalar, kısa ve aralıklı pratiğin uzun ve seyrek seanslardan daha etkili olduğunu göstermektedir.",
    onboarding_notifications_audio: "Pratik hatırlatmaları göndermemi ister misiniz?",
    onboarding_notifications_yes: "Evet, hatırlat",
    onboarding_notifications_no: "Hayır, teşekkürler",
    onboarding_notifications_days_label: "Hangi günler?",
    onboarding_notifications_time_label: "Saat kaçta?",
    onboarding_notifications_email_notice: "Hatırlatmaları e-posta adresinize göndereceğiz.",
    day_mon: "Pzt",
    day_tue: "Sal",
    day_wed: "Çar",
    day_thu: "Per",
    day_fri: "Cum",
    day_sat: "Cmt",
    day_sun: "Paz",
    notifications_summary_enabled: "{{count}} gün için {{time}}'de hatırlatıcılar etkinleştirildi",
    notifications_summary_disabled: "Hatırlatıcılar devre dışı",

    // Calendar section
    onboarding_calendar_description: "Veya takviminize ekleyin",
    onboarding_calendar_google: "Google Takvim",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: ".ics indir",
    onboarding_calendar_outlook_hint: "İpucu: Ekledikten sonra Outlook'ta yinelenen olarak ayarlayın",
    onboarding_calendar_added: "Eklendi",

    // Email checkbox
    onboarding_email_checkbox_label: "Bana e-posta hatırlatıcıları gönder",

    // Warning
    onboarding_no_reminders_warning: "Hatırlatıcı ayarlanmadı. Düzenli pratik daha hızlı öğrenmenize yardımcı olur!",

    // Onboarding step titles
    onboarding_step_native_language: "Ana dil",
    onboarding_step_target_language: "Öğrenmek istediğiniz dili seçin",
    onboarding_step_age: "Yaş",
    onboarding_step_level: "Seviye",
    onboarding_step_pronoun: "Zamirler",
    onboarding_step_objective: "Hedef",
    onboarding_step_notifications: "Hatırlatıcılar",
    onboarding_step_completion: "Tamamla",

    // V2 Steps
    onboarding_welcome_title: "Hoş geldiniz!",
    onboarding_welcome_text: "Birkaç soru ve işle ilgili ilk konuşmanızla başlayalım.",
    onboarding_welcome_duration: "Kurulum yaklaşık 2 dakika sürer, ardından kısa bir ilk konuşma.",
    onboarding_step_ready: "Hazır",
    onboarding_ready_title: "Hazırsınız!",
    onboarding_ready_description: "Ardından, sizi tanıyabilmem ve size ilk kelimenizi öğretebilmem için kısa bir ilk konuşma yapacaksınız.",
    onboarding_ready_learning: "Öğrenilen",
    onboarding_ready_level: "Seviye",
    onboarding_ready_start: "İlk konuşmamı başlat",
    onboarding_ready_not_now: "Şimdi değil",
    onboarding_mic_listening: "Dinliyorum… birkaç kelime söyleyin",
    onboarding_mic_hear_yes: "Kendimi duyabiliyorum",
    onboarding_mic_hear_no: "Kendimi duyamıyorum",
    onboarding_mic_troubleshoot: "Endişelenmeyin — doğru mikrofonun seçili olduğundan ve ses seviyesinin açık olduğundan emin olun, ardından tekrar kaydedin.",
    onboarding_mic_playback_confirm: "Kaydınızı dinleyin — kendinizi duyabiliyor musunuz?",
    onboarding_accept_continue: "Kabul et ve devam et",
    onboarding_back: "Geri",
    onboarding_how_it_works_title: "Nasıl çalışır",
    onboarding_how_it_works_text: "Hem ana dilinizi hem de hedef dilinizi anlıyorum. Öğrenmenize yardımcı olmak için her ikisini de kullanacağım, ama yeni dilinizi mümkün olduğunca kullanmaya çalışın!",
    onboarding_level_beginner_description: "Başlangıç için sözlü beceri",
    level_beginner: "Başlangıç",
    level_intermediate: "Orta",
    level_advanced: "İleri",
    level_beginner_description: "Temel cümleleri ve ifadeleri anlayabilir ve kullanabilirim",
    level_intermediate_description: "Tanıdık konularda sohbet edebilir ve deneyimlerimi anlatabilrim",
    level_advanced_description: "Çoğu durumda kendimi akıcı ve spontan bir şekilde ifade edebilirim",
    onboarding_level_title_v2: "{{language}} dilinde ne yapabilirsiniz?",
    onboarding_level_question: "{{language}} dilinde ne yapabilirsiniz?",
    language_fr: "Fransızca",
    language_en: "İngilizce",
    language_es: "İspanyolca",
    onboarding_level_intermediate_description: "Orta seviye için sözlü beceri",
    onboarding_level_advanced_description: "İleri seviye için sözlü beceri",
    onboarding_adaptation_title: "Sizin için nasıl uyarlanacağım",
    onboarding_adaptation_beginner: "**{{level}}** seçtiğiniz için, ağırlıklı olarak {{nativeLanguage}} konuşacak ve yavaş yavaş {{targetLanguage}} kelimeler ve ifadeler ekleyeceğim. Kelime dağarcığınızı ve temel cümlelerinizi geliştirmeye odaklanacağım.",
    onboarding_adaptation_intermediate: "**{{level}}** seçtiğiniz için, ağırlıklı olarak {{targetLanguage}} konuşacak, gerektiğinde {{nativeLanguage}} açıklamalar yapacağım. Dilbilginizi ve konuşma becerilerinizi geliştirmenize yardımcı olacağım.",
    onboarding_adaptation_advanced: "**{{level}}** seçtiğiniz için, neredeyse tamamen {{targetLanguage}} konuşacağım. İfadenizi, deyimlerinizi ve profesyonel dilinizi geliştirmeye odaklanacağım.",
    onboarding_adaptation_advanced_tip: "💡 İleri düzey bir öğrenici olarak Gerçek zamanlı modu beğenebilirsiniz — daha hızlı, kesintisiz bir konuşma deneyimi. Bunu daha sonra Ayarlar'da etkinleştirebilirsiniz.",
    onboarding_mic_title: "Mikrofonunuzu test edelim",
    onboarding_mic_description: "Konuşmalar için mikrofonunuzu kullanacağız. Çalıştığından emin olalım!",
    onboarding_mic_permission_prompt: "İlk olarak, mikrofonunuza erişmemiz gerekiyor.",
    onboarding_mic_allow: "Mikrofona İzin Ver",
    onboarding_mic_requesting: "Mikrofon erişimi isteniyor...",
    onboarding_mic_denied: "Mikrofon erişimi reddedildi. Lütfen tarayıcı ayarlarınızdan etkinleştirin ve tekrar deneyin.",
    onboarding_mic_retry: "Tekrar dene",
    onboarding_mic_skip: "Bu adımı atla",
    onboarding_mic_ready: "Mikrofonunuz hazır! Kayıt düğmesine tıklayın ve birkaç kelime söyleyin.",
    onboarding_mic_granted: "Mikrofon hazır! Aşağıdaki düğmeye tıklayın ve birkaç kelime söyleyin.",
    onboarding_mic_tip: "İpucu: En iyi sonuçlar için kulaklık ve sessiz bir yer kullanın.",
    onboarding_mic_recording: "Kaydediliyor... Durdurmak için tıklayın",
    onboarding_mic_play_back: "Harika! Kaydınızı dinleyin:",
    onboarding_mic_success: "Mikrofonunuz mükemmel çalışıyor!",
    onboarding_mic_record_again: "Tekrar kaydet",
    onboarding_conversation_title: "İlk konuşmanız",
    onboarding_conversation_start_prompt: "Yeşil düğmeye tıklayın ve başlamak için {{greeting}} deyin!",
    onboarding_conversation_done: "Harika bir konuşmaydı!",
    onboarding_conversation_complete_hint: "Kişiselleştirilmiş öğrenme planınız oluşturuluyor...",
    onboarding_recap_title: "İlk konuşmanızı tamamladınız!",
    onboarding_recap_description: "Bravo! {{language}} dilinde akıcılığa giden ilk adımınızı attınız.",
    onboarding_recap_accomplished: "Başardıklarınız:",
    onboarding_recap_had_conversation: "{{language}} dilinde ilk konuşmanızı yaptınız",
    onboarding_recap_learned_word: "Mesleğiniz için yeni bir kelime öğrendiniz",
    onboarding_recap_personalized: "Öğrenme planınız konuşmanıza göre oluşturuluyor",
    onboarding_auto_objective: "Tanıtım konuşmasına dayalı profesyonel kullanım için {{language}} öğrenin",
    onboarding_save_error: "Bir şeyler ters gitti. İlerlemeniz yerel olarak kaydedildi.",
    onboarding_achievement_title: "{{language}} öğreniminin ilk günü tamamlandı!",
    onboarding_achievement_summary: "Kişiselleştirilmiş {{language}} öğrenme yolunuz hazır. Yolculuğunuza istediğiniz zaman devam edin!",
    onboarding_achievement_continue: "Devam et!",
    onboarding_achievement_stop: "Bugünlük dur",
  },

  ja: {
    // Native Language Step
    onboarding_native_language_title: "あなたの母語は何ですか？",
    onboarding_native_language_description: "母語を選択して、体験をカスタマイズしましょう。",
    onboarding_more_languages: "もっと見る",
    onboarding_show_less_languages: "閉じる",

    // Target Language Step
    onboarding_target_language_title: "何を学びたいですか？",
    onboarding_target_language_description: "学びたい言語を選んでください。短いプレビューが聞けます。",

    // Age Step
    onboarding_age_title: "成人ですか？",
    onboarding_age_yes: "はい",
    onboarding_age_no: "いいえ",
    onboarding_age_adult_description: "18歳以上",
    onboarding_age_child_description: "18歳未満",
    onboarding_age_description: "年齢に適したコンテンツを提供するために役立ちます。",

    // Level Step (CEFR)
    onboarding_level_title: "あなたのレベルは？",
    onboarding_level_description_cefr: "CEFRスケールを使用して習熟度を選択してください。",
    onboarding_level_unknown: "わかりません",
    onboarding_level_placement_hint: "配置テスト後に調整します。",
    cefr_a1_description: "完全な初心者",
    cefr_a2_description: "基本的なフレーズと簡単な会話",
    cefr_b1_description: "身近なトピックについて話せる",
    cefr_b2_description: "ネイティブスピーカーと交流できる",
    cefr_c1_description: "社会的・職業的に流暢",
    cefr_c2_description: "ネイティブに近い熟練度",

    // Pronoun Step
    onboarding_pronoun_title: "代名詞は？",
    onboarding_pronoun_description: "一部の言語には性別があります。正確にお話しするために役立ちます。",
    pronoun_masculine: "彼 (He/Him)",
    pronoun_feminine: "彼女 (She/Her)",
    pronoun_nonbinary: "ノンバイナリー (They/Them)",
    pronoun_unspecified: "回答しない",

    // Terms and Conditions
    terms_checkbox_label: "以下に同意します：",
    terms_and_conditions: "利用規約",

    // Completion Step
    onboarding_completion_title: "準備完了！",
    onboarding_completion_description: "設定の概要です。これらは後でいつでも変更できます。",
    start_learning: "学習を始める",
    saving: "保存中...",
    redirecting: "リダイレクト中...",
    onboarding_privacy_note: "設定は安全に保存され、学習体験のカスタマイズに使用されます。",

    // Learning Objective Step
    onboarding_objective_title: "あなたの目標は？",
    onboarding_objective_description: "達成したいことを教えてください。学習パスをカスタマイズします。",
    onboarding_objective_placeholder: '例：「6ヶ月でクライアントにプロジェクトをプレゼンする。」',
    onboarding_objective_or: "または",
    onboarding_objective_record: "録音",
    onboarding_objective_stop: "停止",
    onboarding_objective_start_recording: "音声録音を開始",
    onboarding_objective_stop_recording: "録音を停止",
    onboarding_objective_transcribing: "文字起こし中...",
    onboarding_objective_mic_denied: "マイクのアクセスが拒否されました。マイクへのアクセスを許可してもう一度お試しください。",
    onboarding_objective_transcription_error: "音声の文字起こしに失敗しました。もう一度お試しいただくか、目標を入力してください。",
    onboarding_objective_skip_hint: "任意です。今はスキップできます。",
    onboarding_objective_entered: "学習目標を入力しました。",
    onboarding_objective_aria_label: "あなたの学習目標",

    // Notifications Step
    onboarding_notifications_title: "継続的に学びましょう",
    onboarding_finish: "完了",
    onboarding_notifications_description: "継続的な練習が鍵です。学習を続けるためのリマインダーを送ります。",
    onboarding_notifications_recommendation: "1日10〜15分の練習は、長くてまれなセッションよりも効果的です。",
    onboarding_notifications_audio: "練習リマインダーを送りましょうか？",
    onboarding_notifications_yes: "リマインドする",
    onboarding_notifications_no: "いいえ",
    onboarding_notifications_days_label: "曜日",
    onboarding_notifications_time_label: "時間",
    onboarding_notifications_email_notice: "メールアドレスにリマインダーを送信します。",
    day_mon: "月",
    day_tue: "火",
    day_wed: "水",
    day_thu: "木",
    day_fri: "金",
    day_sat: "土",
    day_sun: "日",
    notifications_summary_enabled: "{{count}}日分、{{time}}にリマインダーを設定",
    notifications_summary_disabled: "リマインダーは無効です",

    // Calendar section
    onboarding_calendar_description: "またはカレンダーに追加",
    onboarding_calendar_google: "Googleカレンダー",
    onboarding_calendar_outlook: "Outlook",
    onboarding_calendar_download_ics: ".icsをダウンロード",
    onboarding_calendar_outlook_hint: "ヒント：追加後にOutlookで繰り返し設定してください",
    onboarding_calendar_added: "追加済み",

    // Email checkbox
    onboarding_email_checkbox_label: "メールリマインダーを送信する",

    // Warning
    onboarding_no_reminders_warning: "リマインダーが設定されていません。継続が学習を加速させます！",

    // Onboarding step titles
    onboarding_step_native_language: "母語",
    onboarding_step_target_language: "学習言語",
    onboarding_step_age: "年齢",
    onboarding_step_level: "レベル",
    onboarding_step_pronoun: "代名詞",
    onboarding_step_objective: "目標",
    onboarding_step_notifications: "リマインダー",
    onboarding_step_completion: "完了",

    // V2 Steps
    onboarding_welcome_title: "ようこそ！",
    onboarding_welcome_text: "いくつかの質問と最初の仕事に関する会話から始めましょう。",
    onboarding_welcome_duration: "設定は約2分、その後に短い最初の会話があります。",
    onboarding_step_ready: "準備完了",
    onboarding_ready_title: "準備完了！",
    onboarding_ready_description: "次に、短い最初の会話をします。あなたのことを知り、最初の単語をお教えします。",
    onboarding_ready_learning: "学習中",
    onboarding_ready_level: "レベル",
    onboarding_ready_start: "最初の会話を始める",
    onboarding_ready_not_now: "今はしない",
    onboarding_mic_listening: "聞いています…何か話してみてください",
    onboarding_mic_hear_yes: "聞こえます",
    onboarding_mic_hear_no: "聞こえません",
    onboarding_mic_troubleshoot: "ご安心ください — 正しいマイクが選択されているか、音量が上がっているかを確認して、もう一度録音してください。",
    onboarding_mic_playback_confirm: "録音を聞いてみてください — 自分の声が聞こえますか？",
    onboarding_accept_continue: "同意して続ける",
    onboarding_back: "戻る",
    onboarding_how_it_works_title: "使い方",
    onboarding_how_it_works_text: "母語と学習言語の両方を理解します。両方を使って学習をサポートしますが、できるだけ新しい言語を使ってみてください！",
    onboarding_level_beginner_description: "初心者",
    level_beginner: "初心者",
    level_intermediate: "中級",
    level_advanced: "上級",
    level_beginner_description: "基本的なフレーズや表現を理解し使える",
    level_intermediate_description: "身近なトピックについて会話でき、経験を述べられる",
    level_advanced_description: "ほとんどの状況で流暢に自発的に表現できる",
    onboarding_level_title_v2: "{{language}}でどの程度できますか？",
    onboarding_level_question: "{{language}}でどの程度できますか？",
    language_fr: "フランス語",
    language_en: "英語",
    language_es: "スペイン語",
    onboarding_level_intermediate_description: "中級",
    onboarding_level_advanced_description: "上級",
    onboarding_adaptation_title: "あなたに合わせた適応方法",
    onboarding_adaptation_beginner: "**{{level}}**レベルでは、{{nativeLanguage}}をサポートに使いながら、{{targetLanguage}}の基礎を徐々に導入します。",
    onboarding_adaptation_intermediate: "**{{level}}**レベルでは、主に{{targetLanguage}}で話し、複雑な説明の場合のみ{{nativeLanguage}}を使います。",
    onboarding_adaptation_advanced: "**{{level}}**レベルでは、プロフェッショナルな流暢さと慣用表現を磨くために{{targetLanguage}}を使います。",
    onboarding_adaptation_advanced_tip: "ヒント：設定の**リアルタイムモード**で、より速くスムーズな体験をお試しください。",
    onboarding_mic_title: "マイクをテスト",
    onboarding_mic_description: "会話のために音声が聞こえることを確認しましょう。",
    onboarding_mic_permission_prompt: "マイクへのアクセスを許可してください。",
    onboarding_mic_allow: "マイクを許可",
    onboarding_mic_requesting: "アクセスを要求中...",
    onboarding_mic_denied: "アクセスが拒否されました。ブラウザの設定でマイクを有効にしてください。",
    onboarding_mic_retry: "もう一度",
    onboarding_mic_skip: "スキップ",
    onboarding_mic_ready: "準備完了！録音をクリックして、いくつか話してみてください。",
    onboarding_mic_granted: "アクセスが許可されました！下のボタンをクリックしてテスト録音してください。",
    onboarding_mic_tip: "ヒント：最良の結果を得るために、ヘッドフォンと静かな場所を使ってください。",
    onboarding_mic_recording: "録音中...クリックして停止",
    onboarding_mic_play_back: "よくできました！録音を聞いてみてください：",
    onboarding_mic_success: "マイクは正常に動作しています！",
    onboarding_mic_record_again: "もう一度録音",
    onboarding_conversation_title: "最初の会話",
    onboarding_conversation_start_prompt: "ボタンをクリックして**{{greeting}}**と言って始めましょう！",
    onboarding_conversation_done: "よくできました！",
    onboarding_conversation_complete_hint: "パーソナライズされたプランを作成中...",
    onboarding_recap_title: "最初のセッション完了！",
    onboarding_recap_description: "{{language}}の流暢さへの第一歩を踏み出しました。",
    onboarding_recap_accomplished: "まとめ：",
    onboarding_recap_had_conversation: "最初の会話を完了",
    onboarding_recap_learned_word: "仕事の語彙を学習",
    onboarding_recap_personalized: "パーソナライズされたプランを作成",
    onboarding_auto_objective: "紹介会話に基づくプロフェッショナル{{language}}",
    onboarding_save_error: "エラーが発生しました。進捗はローカルに保存されました。",
    onboarding_achievement_title: "{{language}}学習の1日目が完了！",
    onboarding_achievement_summary: "パスの準備ができました。いつでも旅を始められます！",
    onboarding_achievement_continue: "続ける",
    onboarding_achievement_stop: "今日はここまで",
  },
};
